<template>
    <div class="page quiz-edit-page">
        <div class="page-header">
            <div class="page-header-content">
                <h1>
                    {{ id ? '編輯測驗' : '新增測驗'
                    }}{{
                        id && stepOneState.name ? ` - ${stepOneState.name}` : ''
                    }}
                </h1>
                <a-button
                    v-if="currentStep === 1 || currentStep === 0"
                    type="text"
                    class="header-back-button"
                    @click="handleBack"
                >
                    返回
                </a-button>
            </div>
        </div>

        <!-- 步驟區域 - 獨立卡片 -->
        <div class="steps-container">
            <a-steps
                @change="changeStep"
                :current="currentStep"
                :items="stepItems"
            ></a-steps>
        </div>

        <!-- 表單內容區域 - 獨立卡片 -->
        <div class="content light-theme">
            <!-- 步驟一：基本資訊 -->
            <div v-if="currentStep === 0" class="step_one_section">
                <a-form
                    ref="stepOneRef"
                    :model="stepOneState"
                    :rules="stepOneRules"
                    layout="vertical"
                    class="quiz-form"
                >
                    <!-- 第一行：名稱（完整寬度） -->
                    <a-form-item
                        label="名稱"
                        name="name"
                        class="form-item-full"
                    >
                        <a-input
                            v-model:value="stepOneState.name"
                            placeholder="請輸入測驗名稱"
                            size="large"
                        />
                    </a-form-item>

                    <!-- 第三行：計分模式 + 及格分數（左右排版） -->
                    <a-row :gutter="24">
                        <a-col
                            :span="
                                stepOneState.scoring_mode === 'normal' ? 12 : 24
                            "
                        >
                            <a-form-item label="模式" name="scoring_mode">
                                <a-select
                                    ref="mode_select"
                                    v-model:value="stepOneState.scoring_mode"
                                    :options="modeOptions"
                                    placeholder="請選擇模式"
                                    size="large"
                                    @change="handleModeChange"
                                ></a-select>
                            </a-form-item>
                        </a-col>
                        <a-col
                            v-if="stepOneState.scoring_mode === 'normal'"
                            :span="12"
                        >
                            <a-form-item label="及格分數" name="passing_score">
                                <a-input-number
                                    v-model:value="stepOneState.passing_score"
                                    :min="0"
                                    :precision="0"
                                    placeholder="請輸入及格分數"
                                    class="w-full"
                                    size="large"
                                />
                            </a-form-item>
                        </a-col>
                    </a-row>

                    <!-- 第四行：時間限制 + 最大嘗試次數（左右排版） -->
                    <a-row :gutter="24">
                        <a-col :span="12">
                            <a-form-item
                                label="時間限制（分鐘）"
                                name="time_limit_minutes"
                                :help="'測驗的時間限制，以分鐘為單位'"
                            >
                                <a-input-number
                                    v-model:value="timeLimitMinutes"
                                    :min="0"
                                    :precision="0"
                                    placeholder="請輸入時間限制（分鐘）"
                                    class="w-full"
                                    size="large"
                                />
                            </a-form-item>
                        </a-col>
                        <a-col :span="12">
                            <a-form-item
                                label="最大作答次數"
                                name="max_attempts_per_user"
                                :help="'每位用戶可以作答此測驗的次數'"
                            >
                                <a-input-number
                                    v-model:value="
                                        stepOneState.max_attempts_per_user
                                    "
                                    :min="0"
                                    :precision="0"
                                    placeholder="請輸入最大作答次數"
                                    class="w-full"
                                    size="large"
                                />
                            </a-form-item>
                        </a-col>
                    </a-row>

                    <!-- 最後一行：說明（完整寬度，使用 textarea） -->
                    <a-form-item
                        label="說明"
                        class="form-item-full"
                        :help="'測驗的詳細說明'"
                    >
                        <a-textarea
                            v-model:value="stepOneState.description"
                            placeholder="請輸入測驗說明"
                            :rows="4"
                            :maxlength="500"
                            show-count
                            size="large"
                        />
                    </a-form-item>

                    <!-- 提交按鈕 -->
                    <a-form-item class="form-item-full submit-item">
                        <a-button
                            type="primary"
                            class="submit-button next-button"
                            @click="saveOneStep"
                            block
                            size="large"
                        >
                            <span>下一步</span>
                        </a-button>
                    </a-form-item>
                </a-form>
            </div>
            <!-- 步驟二：維度設置（僅維度模式） -->
            <div
                v-if="
                    currentStep === 1 &&
                    stepOneState.scoring_mode === 'dimensions'
                "
                class="step_dimensions_section"
            >
                <div class="dimensions-container">
                    <!-- 維度列表 -->
                    <div class="dimensions-list">
                        <div
                            v-for="(dimension, idx) in dimensionsState"
                            :key="dimension.id || idx"
                            class="dimension-item-wrapper"
                        >
                            <div class="dimension-card">
                                <a-button
                                    type="text"
                                    danger
                                    class="delete-dimension-button"
                                    @click="deleteDimensionHandler(idx)"
                                    title="刪除維度"
                                >
                                    <template #icon>
                                        <DeleteOutlined />
                                    </template>
                                </a-button>
                                <div class="dimension-content">
                                    <a-row :gutter="16">
                                        <a-col :span="24">
                                            <div class="dimension-field">
                                                <label class="dimension-label">
                                                    名稱
                                                    <span class="required-star">
                                                        *
                                                    </span>
                                                </label>
                                                <a-input
                                                    v-model:value="
                                                        dimension.name
                                                    "
                                                    placeholder="請輸入維度名稱"
                                                    size="middle"
                                                />
                                            </div>
                                        </a-col>
                                    </a-row>
                                    <a-row :gutter="16">
                                        <a-col :span="24">
                                            <div class="dimension-field">
                                                <label class="dimension-label">
                                                    說明
                                                </label>
                                                <a-textarea
                                                    v-model:value="
                                                        dimension.description
                                                    "
                                                    placeholder="請輸入維度說明（選填）"
                                                    :rows="2"
                                                    size="middle"
                                                />
                                            </div>
                                        </a-col>
                                    </a-row>
                                </div>
                            </div>
                        </div>

                        <!-- 新增維度按鈕（在列表下方，方便多維度時使用） -->
                        <div class="add-dimension-wrapper">
                            <a-button
                                type="dashed"
                                class="add-dimension-button"
                                @click="createNewDimension"
                                size="large"
                                block
                            >
                                <template #icon>
                                    <PlusOutlined />
                                </template>
                                新增維度
                            </a-button>
                        </div>
                    </div>

                    <!-- 操作按鈕 -->
                    <a-form-item class="form-item-full submit-item">
                        <a-button
                            type="primary"
                            class="submit-button next-button"
                            :loading="loading"
                            block
                            size="large"
                            @click="saveDimensions"
                        >
                            <span>下一步</span>
                        </a-button>
                    </a-form-item>
                </div>
            </div>

            <!-- 步驟三：結果卡設置（僅維度模式） -->
            <div
                v-if="
                    currentStep === 2 &&
                    stepOneState.scoring_mode === 'dimensions'
                "
                class="step_results_section"
            >
                <div class="results-header">
                    <h3 class="results-title">結果卡設置</h3>
                    <div class="results-info">
                        共需設置
                        <span class="dimension-count">
                            {{ validDimensionsCount }}
                        </span>
                        個維度的結果卡
                    </div>
                </div>

                <div class="results-list">
                    <div
                        v-for="(dimension, idx) in validDimensions"
                        :key="dimension.id || idx"
                        class="result-card-item"
                    >
                        <div class="result-card-header">
                            <div class="result-card-dimension">
                                <span class="dimension-badge">
                                    維度 {{ idx + 1 }}
                                </span>
                                <span class="dimension-name">
                                    {{ dimension.name }}
                                </span>
                            </div>
                        </div>
                        <a-form
                            v-if="resultsStateList[idx]"
                            :ref="(el) => setResultFormRef(el, idx)"
                            :model="resultsStateList[idx]"
                            :rules="resultsRules"
                            layout="vertical"
                            class="result-card-form"
                        >
                            <!-- 標題 -->
                            <a-form-item
                                :label="`標題`"
                                :name="[idx, 'title']"
                                class="form-item-full"
                            >
                                <a-input
                                    v-model:value="resultsStateList[idx].title"
                                    :placeholder="
                                        getTitlePlaceholder(dimension)
                                    "
                                    size="large"
                                />
                            </a-form-item>

                            <!-- 副標題 -->
                            <a-form-item
                                :label="`副標題`"
                                :name="[idx, 'subtitle']"
                                class="form-item-full"
                            >
                                <a-input
                                    v-model:value="
                                        resultsStateList[idx].subtitle
                                    "
                                    :placeholder="
                                        getSubtitlePlaceholder(dimension)
                                    "
                                    size="large"
                                />
                            </a-form-item>

                            <!-- 概述 -->
                            <a-form-item
                                :label="`概述`"
                                :name="[idx, 'summary']"
                                class="form-item-full"
                            >
                                <a-textarea
                                    v-model:value="
                                        resultsStateList[idx].summary
                                    "
                                    :placeholder="
                                        getSummaryPlaceholder(dimension)
                                    "
                                    :rows="4"
                                    :maxlength="1000"
                                    show-count
                                    size="large"
                                />
                            </a-form-item>

                            <!-- 圖片上傳 -->
                            <a-form-item
                                :label="`圖片`"
                                :name="[idx, 'image']"
                                class="form-item-full"
                            >
                                <a-upload
                                    v-model:file-list="
                                        resultsStateList[idx].imageFileList
                                    "
                                    :before-upload="
                                        (file) => handleBeforeUpload(file, idx)
                                    "
                                    :remove="() => handleRemoveImage(idx)"
                                    @error="handleImageError"
                                    @change="
                                        (info) => handleImageChange(info, idx)
                                    "
                                    accept="image/*"
                                    list-type="picture-card"
                                    :max-count="1"
                                >
                                    <div
                                        v-if="
                                            !resultsStateList[idx]
                                                .imageFileList ||
                                            resultsStateList[idx].imageFileList
                                                .length === 0
                                        "
                                    >
                                        <UploadOutlined />
                                        <div class="ant-upload-text">上傳</div>
                                    </div>
                                </a-upload>
                            </a-form-item>
                        </a-form>
                    </div>
                </div>

                <!-- 提交按鈕 -->
                <a-form-item class="form-item-full submit-item">
                    <a-button
                        type="primary"
                        class="submit-button next-button"
                        :loading="loading"
                        @click="saveResults"
                        block
                        size="large"
                    >
                        <span>下一步</span>
                    </a-button>
                </a-form-item>
            </div>

            <!-- 步驟三/四：題目設定 -->
            <div
                v-if="
                    currentStep ===
                    (stepOneState.scoring_mode === 'dimensions' ? 3 : 1)
                "
                class="step_two_section"
            >
                <!-- 空狀態：尚未新增題目 -->
                <div
                    v-if="stepTwoState.length === 0"
                    class="empty-questions-state"
                >
                    <div class="empty-state-content">
                        <div class="empty-state-icon">📝</div>
                        <h3 class="empty-state-title">尚未新增題目</h3>
                        <p class="empty-state-description">
                            請點擊「新增題目」按鈕開始建立測驗題目
                        </p>
                        <a-button
                            type="primary"
                            class="empty-state-button"
                            @click="createNewQuestion"
                            size="large"
                        >
                            新增第一題
                        </a-button>
                    </div>
                </div>

                <!-- 題目列表容器 - 可拖動排序 -->
                <div class="questions-container" ref="questionsContainerRef">
                    <div
                        v-for="(two, idx) in stepTwoState"
                        :key="`question-${idx}`"
                        class="step_two_section_card question-card"
                        :data-index="idx"
                    >
                        <div class="question-header">
                            <div class="question-header-left">
                                <HolderOutlined class="drag-handle" />
                                <h2 class="question-title">
                                    <span
                                        v-if="
                                            two.is_required === '1' ||
                                            two.is_required === true
                                        "
                                        class="required-star"
                                    >
                                        *
                                    </span>
                                </h2>
                            </div>
                            <div class="question-header-middle">
                                <div
                                    v-if="
                                        stepOneState.scoring_mode !==
                                            'survey' &&
                                        stepOneState.scoring_mode !==
                                            'dimensions'
                                    "
                                    class="header-field-item"
                                >
                                    <span class="header-field-label">
                                        需審核
                                    </span>
                                    <a-radio-group
                                        v-model:value="two.scoring_mode"
                                        name="radioGroup"
                                        size="small"
                                        class="header-radio-group"
                                    >
                                        <a-radio value="auto">否</a-radio>
                                        <a-radio value="manual">是</a-radio>
                                    </a-radio-group>
                                </div>
                                <div class="header-field-item">
                                    <span class="header-field-label">
                                        是否必填
                                    </span>
                                    <a-switch
                                        :checked="
                                            two.is_required === '1' ||
                                            two.is_required === true
                                        "
                                        checked-children="是"
                                        unchecked-children="否"
                                        size="small"
                                        @change="
                                            (checked) =>
                                                (two.is_required = checked
                                                    ? '1'
                                                    : '0')
                                        "
                                    />
                                </div>
                            </div>
                            <div class="question-header-actions">
                                <a-button
                                    type="text"
                                    class="question-action-button duplicate-question-button"
                                    @click="duplicateQuestion(idx)"
                                    title="複製題目"
                                >
                                    <template #icon>
                                        <CopyOutlined />
                                    </template>
                                </a-button>
                                <a-button
                                    type="text"
                                    class="question-action-button delete-question-button"
                                    @click="deleteQuestion(idx)"
                                    title="刪除題目"
                                >
                                    <template #icon>
                                        <DeleteOutlined />
                                    </template>
                                </a-button>
                                <a-button
                                    type="text"
                                    class="question-action-button add-question-inline-button"
                                    @click="createNewQuestion"
                                    title="新增題目"
                                >
                                    <template #icon>
                                        <PlusOutlined />
                                    </template>
                                </a-button>
                            </div>
                        </div>
                        <a-form
                            ref="stepTwoRef"
                            :model="two"
                            layout="vertical"
                            :rules="stepTwoRules"
                            class="question-form"
                        >
                            <!-- 第一行：問題 + 類型（左右排版） -->
                            <a-row :gutter="16">
                                <a-col :span="16">
                                    <a-form-item label="問題" name="title">
                                        <a-input
                                            v-model:value="two.title"
                                            placeholder="請輸入問題"
                                            size="large"
                                        />
                                    </a-form-item>
                                </a-col>
                                <a-col :span="8">
                                    <a-form-item
                                        label="類型"
                                        name="question_type"
                                    >
                                        <a-select
                                            ref="question_type"
                                            v-model:value="two.question_type"
                                            :options="questionTypesOptions"
                                            placeholder="請選擇類型"
                                            size="large"
                                            @change="
                                                handleQuestionTypeChange(two)
                                            "
                                        ></a-select>
                                    </a-form-item>
                                </a-col>
                            </a-row>

                            <!-- 第二行：題目描述（完整寬度，可選） -->
                            <a-form-item label="描述" class="form-item-full">
                                <a-input
                                    v-model:value="two.description"
                                    placeholder="請輸入題目描述"
                                    size="large"
                                />
                            </a-form-item>

                            <div
                                v-if="
                                    two.question_type === 'single' ||
                                    two.question_type === 'multiple'
                                "
                                class="options-container"
                                :ref="(el) => setOptionContainerRef(el, idx)"
                            >
                                <!-- 選項列表 -->
                                <div
                                    v-for="(option, optIdx) in two.options"
                                    :key="optIdx"
                                    class="option-item"
                                    :data-option-index="optIdx"
                                >
                                    <a-form-item
                                        :name="['options', optIdx, 'label']"
                                        :rules="{
                                            required: true,
                                            message: '選項需填入',
                                            trigger: 'change'
                                        }"
                                        class="option-form-item"
                                    >
                                        <a-row :gutter="16" align="middle">
                                            <!-- 拖動手柄 -->
                                            <a-col
                                                :span="1"
                                                class="option-drag-col"
                                            >
                                                <HolderOutlined
                                                    class="option-drag-handle"
                                                />
                                            </a-col>

                                            <!-- 單選/多選圖標 -->
                                            <a-col
                                                :span="2"
                                                class="option-icon-col"
                                            >
                                                <div
                                                    class="option-type-icon"
                                                    :class="
                                                        two.question_type ===
                                                        'single'
                                                            ? 'radio-icon'
                                                            : 'checkbox-icon'
                                                    "
                                                >
                                                    <template
                                                        v-if="
                                                            two.question_type ===
                                                            'single'
                                                        "
                                                    >
                                                        <!-- 圓形單選圖標 -->
                                                        <div
                                                            class="radio-circle"
                                                        ></div>
                                                    </template>
                                                    <template v-else>
                                                        <!-- 方形多選圖標 -->
                                                        <div
                                                            class="checkbox-square"
                                                        ></div>
                                                    </template>
                                                </div>
                                            </a-col>

                                            <!-- 輸入框 -->
                                            <a-col
                                                :span="21"
                                                class="option-input-col"
                                            >
                                                <a-input
                                                    v-model:value="
                                                        two.options[optIdx][
                                                            'label'
                                                        ]
                                                    "
                                                    placeholder="新增選項"
                                                    size="large"
                                                    class="option-input"
                                                >
                                                    <template #suffix>
                                                        <CloseOutlined
                                                            v-if="
                                                                two.options
                                                                    .length > 2
                                                            "
                                                            class="option-delete-icon"
                                                            @click="
                                                                delOpt(
                                                                    two.options,
                                                                    optIdx,
                                                                    two.question_type
                                                                )
                                                            "
                                                        />
                                                    </template>
                                                </a-input>
                                            </a-col>
                                        </a-row>
                                    </a-form-item>
                                </div>

                                <!-- 新增選項按鈕（顯示在所有選項下方） -->
                                <div class="add-option-button-row">
                                    <a-row :gutter="16" align="middle">
                                        <a-col :span="1"></a-col>
                                        <a-col :span="2"></a-col>
                                        <a-col :span="21">
                                            <a-button
                                                type="dashed"
                                                class="add-option-button-text"
                                                @click="createNewOption(idx)"
                                                block
                                            >
                                                <PlusOutlined />
                                                新增選項
                                            </a-button>
                                        </a-col>
                                    </a-row>
                                </div>
                            </div>
                        </a-form>
                    </div>
                </div>

                <a-form-item class="form-item-full submit-item">
                    <a-button
                        type="primary"
                        class="submit-button next-button"
                        @click="saveTwoSubmit"
                        block
                        size="large"
                    >
                        <span>下一步</span>
                    </a-button>
                </a-form-item>
            </div>

            <!-- 第三步：完成建立 / 發布 -->
            <!-- 步驟四/五：完成 -->
            <div
                v-if="
                    currentStep ===
                    (stepOneState.scoring_mode === 'dimensions' ? 4 : 2)
                "
                class="step_three_section"
            >
                <div class="step-three-content">
                    <!-- 任務列表 -->
                    <div class="task-list">
                        <div class="task-item task-completed">
                            <span class="task-icon">✓</span>
                            <span class="task-text">測驗設置完成</span>
                        </div>
                        <div
                            v-if="stepOneState.scoring_mode !== 'survey'"
                            :class="[
                                'task-item',
                                canPublishNonSurvey && !checkingPublishStatus
                                    ? 'task-completed'
                                    : 'task-pending'
                            ]"
                        >
                            <span class="task-icon">
                                {{
                                    canPublishNonSurvey &&
                                    !checkingPublishStatus
                                        ? '✓'
                                        : '✗'
                                }}
                            </span>
                            <span class="task-text">
                                {{
                                    stepOneState.scoring_mode === 'dimensions'
                                        ? '維度加權設置'
                                        : '答案設置'
                                }}
                            </span>
                        </div>
                    </div>

                    <!-- 說明文字 -->
                    <div class="step-three-description">
                        <template v-if="stepOneState.scoring_mode === 'survey'">
                            <p class="description-text">
                                問卷模式不需要設定答案，可以直接發布或儲存為草稿。
                            </p>
                        </template>
                        <template v-else>
                            <template v-if="checkingPublishStatus">
                                <p class="description-text">
                                    正在檢查測驗的{{
                                        stepOneState.scoring_mode ===
                                        'dimensions'
                                            ? '加權設置'
                                            : '答案設置'
                                    }}狀態...
                                </p>
                            </template>
                            <template v-else-if="canPublishNonSurvey">
                                <p class="description-text success-text">
                                    ✓
                                    測驗基本資訊與答案皆已設置完成，可以發布測驗了！
                                </p>
                                <p class="description-subtext">
                                    發布後的測驗將開放給使用者作答。你也可以選擇儲存為草稿，稍後再發布。
                                </p>
                            </template>
                            <template v-else>
                                <p class="description-text warning-text">
                                    ✗ 測驗題目尚未設置{{
                                        stepOneState.scoring_mode ===
                                        'dimensions'
                                            ? '維度加權'
                                            : '答案'
                                    }}，無法發布。
                                </p>
                                <p class="description-subtext">
                                    請先前往「{{
                                        stepOneState.scoring_mode ===
                                        'dimensions'
                                            ? '設定維度加權'
                                            : '設定答案'
                                    }}」頁面為題目設置完成後即可發布。
                                </p>
                            </template>
                        </template>
                    </div>

                    <!-- 問卷模式：可以選擇發布或儲存草稿 -->
                    <template v-if="stepOneState.scoring_mode === 'survey'">
                        <div class="step-three-actions">
                            <a-space size="large">
                                <a-button
                                    size="large"
                                    class="h-[44px] save-draft-button"
                                    :loading="savingDraft"
                                    @click="handleSaveDraft"
                                >
                                    儲存草稿
                                </a-button>
                                <a-button
                                    type="primary"
                                    size="large"
                                    class="h-[44px] publish-button"
                                    :loading="publishing"
                                    @click="handlePublish"
                                >
                                    發布
                                </a-button>
                            </a-space>
                        </div>
                    </template>

                    <!-- 測驗/維度模式：根據答案狀態顯示不同選項 -->
                    <template v-else>
                        <div
                            v-if="checkingPublishStatus"
                            class="checking-status"
                        >
                            正在檢查發布狀態...
                        </div>
                        <div v-else class="step-three-actions">
                            <a-space size="large">
                                <a-button
                                    size="large"
                                    class="h-[44px] save-draft-button"
                                    :loading="savingDraft"
                                    @click="handleSaveDraft"
                                >
                                    儲存草稿
                                </a-button>
                                <a-button
                                    v-if="
                                        canPublishNonSurvey &&
                                        stepOneState.has_answer === true
                                    "
                                    type="primary"
                                    size="large"
                                    class="h-[44px] publish-button"
                                    :loading="publishing"
                                    @click="handlePublish"
                                >
                                    發布
                                </a-button>
                                <a-button
                                    v-else
                                    type="primary"
                                    size="large"
                                    class="h-[44px] go-to-answers-button"
                                    @click="goToSetAnswers"
                                >
                                    前往{{
                                        stepOneState.scoring_mode ===
                                        'dimensions'
                                            ? '設定加權'
                                            : '設定答案'
                                    }}
                                </a-button>
                            </a-space>
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import {
    ref,
    reactive,
    onMounted,
    onBeforeUnmount,
    computed,
    watch,
    nextTick
} from 'vue';
import Sortable from 'sortablejs';
import { message, Modal } from 'ant-design-vue';
import { showErrorMessage } from '@/utils/messageHelper';
import {
    CloseOutlined,
    RightOutlined,
    PlusOutlined,
    CopyOutlined,
    HolderOutlined,
    CheckCircleOutlined,
    DeleteOutlined,
    UploadOutlined
} from '@ant-design/icons-vue';
import {
    createQuizzes,
    createQuestion,
    createBatchQuestions,
    getSingleQuiz,
    updateQuizzes,
    updateQuestion,
    deleteQuestion as deleteQuestionAPI,
    getPublishReport,
    createDimensions,
    updateDimension,
    deleteDimension,
    createQuizResult,
    getQuizResults,
    updateQuizResult,
    deleteQuizResult
} from '@/api/quiz';
import { useRouter, useRoute } from 'vue-router';

const router = useRouter();
const route = useRoute();
const { params } = route;
const { id } = params;

const loading = ref(false);
const currentStep = ref(0);

const stepOneRef = ref();
const stepTwoRef = ref();

const stepOneState = ref({
    name: null,
    description: null,
    status: null,
    scoring_mode: null,
    passing_score: null,
    time_limit_seconds: null,
    max_attempts_per_user: 0 // 預設為 0
});

// 時間限制（分鐘）- 用於前端顯示和輸入
const timeLimitMinutes = computed({
    get: () => {
        if (
            stepOneState.value.time_limit_seconds === null ||
            stepOneState.value.time_limit_seconds === undefined
        ) {
            return undefined;
        }
        // 將秒數轉換為分鐘
        return Math.round(stepOneState.value.time_limit_seconds / 60);
    },
    set: (value) => {
        if (value === null || value === undefined) {
            stepOneState.value.time_limit_seconds = null;
        } else {
            // 將分鐘轉換為秒數
            stepOneState.value.time_limit_seconds = value * 60;
        }
    }
});

const stepTwoState = ref([
    {
        title: '',
        question_type: 'single',
        description: '',
        is_required: '1',
        scoring_mode: 'auto', // 預設不需審核
        options: [
            {
                label: ''
            }
        ]
    }
]);

// 維度數據（預設一個維度）
const dimensionsState = ref([
    {
        name: '',
        description: ''
    }
]);

// 結果卡數據（每個維度一個結果卡）
const resultsStateList = ref([]);

// 有效維度（有 ID 和名稱的維度）
const validDimensions = computed(() => {
    return dimensionsState.value.filter(
        (d) => d.id && d.name && d.name.trim() !== ''
    );
});

// 有效維度數量
const validDimensionsCount = computed(() => {
    return validDimensions.value.length;
});

// 結果卡表單驗證規則
const resultsRules = {
    title: [{ required: true, message: '請輸入標題' }]
};

// 結果卡表單 refs
const resultFormRefs = ref([]);

// 設置結果卡表單 ref
const setResultFormRef = (el, idx) => {
    if (el) {
        resultFormRefs.value[idx] = el;
    }
};

// 根據維度生成標題 placeholder
const getTitlePlaceholder = (dimension) => {
    const dimensionName = dimension?.name || '維度';
    return `例如：${dimensionName}型人格`;
};

// 根據維度生成副標題 placeholder
const getSubtitlePlaceholder = (dimension) => {
    const dimensionName = dimension?.name || '維度';
    return `例如：你是一個典型的${dimensionName}型人格`;
};

// 根據維度生成概述 placeholder
const getSummaryPlaceholder = (dimension) => {
    const dimensionName = dimension?.name || '維度';
    return `例如：根據測驗結果，你在${dimensionName}這個維度上得分最高。這意味著你具有${dimensionName}的特質，通常表現為...`;
};

// 獲取 Base64 預覽
const getBase64 = (img, callback) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => callback(reader.result));
    reader.readAsDataURL(img);
};

// 壓縮圖片
const compressImage = (
    file,
    maxWidth = 1920,
    maxHeight = 1920,
    quality = 0.75
) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (e) => {
            const img = new Image();
            img.src = e.target.result;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;
                const originalSize = file.size;

                // 計算縮放比例
                if (width > maxWidth || height > maxHeight) {
                    const ratio = Math.min(
                        maxWidth / width,
                        maxHeight / height
                    );
                    width = width * ratio;
                    height = height * ratio;
                }

                canvas.width = width;
                canvas.height = height;

                const ctx = canvas.getContext('2d');
                // 提高圖片渲染品質
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';
                ctx.drawImage(img, 0, 0, width, height);

                // 根據檔案類型選擇最佳格式和品質
                let outputType = file.type;
                let outputQuality = quality;

                // 對於 PNG，如果檔案很大，考慮轉換為 JPEG
                if (file.type === 'image/png' && originalSize > 500 * 1024) {
                    outputType = 'image/jpeg';
                    outputQuality = 0.8;
                }

                // 轉換為 Blob
                canvas.toBlob(
                    (blob) => {
                        if (blob) {
                            // 檢查壓縮後的檔案大小
                            // 如果壓縮後比原始檔案大，使用原始檔案
                            if (blob.size >= originalSize) {
                                console.log(
                                    `壓縮後檔案變大（${(blob.size / 1024).toFixed(2)}KB >= ${(originalSize / 1024).toFixed(2)}KB），使用原始檔案`
                                );
                                resolve(file);
                                return;
                            }

                            // 創建新的 File 對象
                            const compressedFile = new File([blob], file.name, {
                                type: outputType,
                                lastModified: Date.now()
                            });
                            resolve(compressedFile);
                        } else {
                            resolve(file);
                        }
                    },
                    outputType,
                    outputQuality
                );
            };
            img.onerror = () => {
                reject(new Error('圖片載入失敗'));
            };
        };
        reader.onerror = () => {
            reject(new Error('檔案讀取失敗'));
        };
    });
};

// 驗證圖片檔案
const validateImageFile = (file) => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/webp'
    ];

    // 檢查檔案大小
    if (file.size > maxSize) {
        const fileSizeMB = (file.size / 1024 / 1024).toFixed(2);
        return {
            valid: false,
            message: `圖片大小（${fileSizeMB}MB）超過 5MB 限制`
        };
    }

    // 檢查檔案類型
    if (!allowedTypes.includes(file.type)) {
        return {
            valid: false,
            message: '不支援的圖片格式，請使用 JPG、PNG、GIF 或 WebP'
        };
    }

    return { valid: true };
};

// 處理檔案上傳前驗證
const handleBeforeUpload = (file, idx) => {
    const validation = validateImageFile(file);
    if (!validation.valid) {
        showErrorMessage(validation.message);
        return false;
    }

    // 阻止自動上傳，我們會在 change 事件中處理壓縮
    return false;
};

// 處理圖片上傳變更（包含壓縮）
const handleImageChange = async (info, idx) => {
    // 獲取檔案：優先使用 originFileObj，如果沒有則使用 file 本身
    let originalFile = null;

    if (info.file) {
        // 當 before-upload 返回 false 時，檔案可能沒有 originFileObj，直接使用 file
        originalFile = info.file.originFileObj || info.file;
    }

    if (!originalFile) {
        return;
    }

    // 先立即保存原始檔案並顯示預覽（確保用戶能立即看到）
    if (info.fileList && info.fileList.length > 0) {
        // 更新 file list
        resultsStateList.value[idx].imageFileList = info.fileList;
    }

    // 檢查檔案大小，如果超過 1MB 進行壓縮
    const fileSizeMB = originalFile.size / 1024 / 1024;
    const needsCompression = fileSizeMB > 1;

    if (needsCompression) {
        message.loading('圖片大小超過 1MB，正在壓縮...', 0);
        try {
            // 壓縮圖片
            const compressedFile = await compressImage(originalFile);
            const originalSizeKB = (originalFile.size / 1024).toFixed(2);
            const compressedSizeKB = (compressedFile.size / 1024).toFixed(2);
            const compressionRatio = (
                ((originalFile.size - compressedFile.size) /
                    originalFile.size) *
                100
            ).toFixed(1);
            message.destroy(); // 關閉 loading

            // 檢查是否真的壓縮了
            if (compressedFile.size < originalFile.size) {
                message.success(
                    `圖片已壓縮：${originalSizeKB}KB → ${compressedSizeKB}KB (減少 ${compressionRatio}%)`
                );
            } else {
                message.info('圖片已優化，檔案大小未變');
            }

            // 更新 file list 中的文件
            if (info.fileList && info.fileList.length > 0) {
                // 更新 originFileObj
                if (info.fileList[0].originFileObj !== undefined) {
                    info.fileList[0].originFileObj = compressedFile;
                } else {
                    Object.assign(info.fileList[0], {
                        originFileObj: compressedFile
                    });
                }
            }

            // 更新 file list
            resultsStateList.value[idx].imageFileList = info.fileList;
        } catch (error) {
            console.error('圖片壓縮失敗:', error);
            message.destroy(); // 關閉 loading
            message.warning('圖片壓縮失敗，使用原始圖片');
        }
    } else {
        // 如果檔案不大，檢查圖片尺寸，如果過大也進行壓縮
        const img = new Image();
        const objectUrl = URL.createObjectURL(originalFile);
        img.onload = async () => {
            URL.revokeObjectURL(objectUrl);
            if (img.width > 1920 || img.height > 1920) {
                try {
                    message.loading('圖片尺寸過大，正在壓縮...', 0);
                    const compressedFile = await compressImage(originalFile);
                    message.destroy();
                    message.success('圖片已優化');
                    if (info.fileList && info.fileList.length > 0) {
                        if (info.fileList[0].originFileObj !== undefined) {
                            info.fileList[0].originFileObj = compressedFile;
                        } else {
                            Object.assign(info.fileList[0], {
                                originFileObj: compressedFile
                            });
                        }
                    }
                    // 更新 file list
                    resultsStateList.value[idx].imageFileList = info.fileList;
                } catch (error) {
                    console.error('圖片壓縮失敗:', error);
                    message.destroy();
                    message.warning('圖片壓縮失敗，使用原始圖片');
                }
            }
        };
        img.src = objectUrl;
    }
};

// 移除圖片（同時清理 blob URL）
const handleRemoveImage = (idx) => {
    if (
        resultsStateList.value[idx] &&
        resultsStateList.value[idx].imageFileList.length > 0
    ) {
        // 清理 blob URL
        const oldUrl = resultsStateList.value[idx].imageFileList[0]?.url;
        if (oldUrl && oldUrl.startsWith('blob:')) {
            URL.revokeObjectURL(oldUrl);
        }
        resultsStateList.value[idx].imageFileList = [];
    }
};

// 處理圖片載入錯誤
const handleImageError = (error, file, fileList) => {
    // 當圖片載入失敗時，提示用戶但不自動清除（讓用戶決定是否重新上傳）
    const idx = resultsStateList.value.findIndex((result) =>
        result.imageFileList.some((f) => f.uid === file.uid)
    );
    if (idx !== -1) {
        // 更新圖片狀態為錯誤
        const fileIndex = resultsStateList.value[idx].imageFileList.findIndex(
            (f) => f.uid === file.uid
        );
        if (fileIndex !== -1) {
            resultsStateList.value[idx].imageFileList[fileIndex].status =
                'error';
        }
        // 只在首次載入時提示，避免重複提示
        if (!resultsStateList.value[idx]._errorShown) {
            message.warning('部分圖片無法載入，請檢查圖片路徑或重新上傳');
            resultsStateList.value[idx]._errorShown = true;
        }
    }
};

// 步驟定義（根據模式動態生成）
const stepItems = computed(() => {
    const items = [{ title: '基本資訊' }];

    // 如果是維度模式，添加「設置維度」和「結果卡設置」步驟
    if (stepOneState.value.scoring_mode === 'dimensions') {
        items.push({ title: '設置維度' });
        items.push({ title: '設置結果' });
    }

    items.push({ title: '題目設定' });
    items.push({ title: '完成' });

    return items;
});

const questionsContainerRef = ref(null);
let sortableInstance = null;

// 選項排序實例（每個題目一個）
const optionSortableInstances = ref({});
const optionContainerRefs = ref({});

// 設置選項容器 ref
const setOptionContainerRef = (el, questionIdx) => {
    if (el) {
        optionContainerRefs.value[questionIdx] = el;
        nextTick(() => {
            initOptionSortable(questionIdx);
        });
    }
};

const stepOneRules = {
    name: [{ required: true, message: '請輸入名稱' }],
    scoring_mode: [{ required: true, message: '請選擇審核模式' }],
    passing_score: [
        {
            validator: (rule, value) => {
                // 允許空值
                if (value === null || value === undefined || value === '') {
                    return Promise.resolve();
                }
                // 轉換為數字進行檢查
                const numValue = Number(value);
                // 檢查是否為有效數字
                if (isNaN(numValue)) {
                    return Promise.reject('請輸入有效的數字');
                }
                // 檢查是否為負數
                if (numValue < 0) {
                    return Promise.reject('及格分數不能為負數');
                }
                return Promise.resolve();
            }
        }
    ],
    time_limit_minutes: [
        {
            type: 'number',
            min: 0,
            message: '時間限制不能為負數'
        }
    ],
    max_attempts_per_user: [
        {
            type: 'number',
            min: 0,
            message: '最大嘗試次數不能為負數'
        }
    ]
};

const stepTwoRules = {
    title: [{ required: true, message: '請輸入名稱' }],
    question_type: [{ required: true, message: '請選取題型' }]
};

const modeOptions = [
    { label: '測驗', value: 'normal' },
    { label: '多維', value: 'dimensions' },
    { label: '問卷', value: 'survey' }
];

// 題目類型選項（根據測驗模式動態變化）
const questionTypesOptions = computed(() => {
    // 多維度模式只能選擇單選題
    if (stepOneState.value.scoring_mode === 'dimensions') {
        return [{ label: '單選題', value: 'single' }];
    }
    // 其他模式可以選擇所有類型
    return [
        { label: '單選題', value: 'single' },
        { label: '多選題', value: 'multiple' },
        { label: '短文字', value: 'text' },
        { label: '數字', value: 'number' }
        // { label: '量表', value: 'scale' }
    ];
});

const questionScoringModeOptions = [
    { label: '手動', value: 'auto' },
    { label: '自動', value: 'manual' }
];

const questionValidationRuleOptions = [
    { label: '無', value: 'none' },
    { label: '數字範圍', value: 'number_range' },
    { label: '文字長度', value: 'text_length' },
    { label: '正規', value: 'regex' }
];

const handleModeChange = (e) => {
    stepOneState.value.scoring_mode = e;

    // 如果切換到多維度模式，將所有題目類型自動調整為單選題，並設定為不需審核（auto）
    if (e === 'dimensions') {
        stepTwoState.value.forEach((question) => {
            // 自動設定為不需審核
            question.scoring_mode = 'auto';

            if (question.question_type !== 'single') {
                question.question_type = 'single';

                // 如果是文字或數字題，需要添加選項
                if (!question.options || question.options.length === 0) {
                    question.options = [{ label: '' }];
                }

                // 重新初始化選項排序
                const questionIdx = stepTwoState.value.findIndex(
                    (q) => q === question
                );
                if (questionIdx !== -1) {
                    nextTick(() => {
                        initOptionSortable(questionIdx);
                    });
                }
            }
        });
    }
};

const createNewQuestion = (e) => {
    const newQuestion = {
        title: '',
        question_type: 'single',
        description: '',
        is_required: '1',
        scoring_mode: 'auto', // 預設不需審核（否）
        options: [
            {
                label: ''
            }
        ]
    };

    // 問卷模式：需審核（是）
    if (stepOneState.value.scoring_mode === 'survey') {
        newQuestion.scoring_mode = 'manual';
    }

    // 維度模式：不需審核（auto）
    if (stepOneState.value.scoring_mode === 'dimensions') {
        newQuestion.scoring_mode = 'auto';
    }

    stepTwoState.value.push(newQuestion);
    // 重新初始化排序
    nextTick(() => {
        initSortable();
    });
};

// 複製題目
const duplicateQuestion = (idx) => {
    const original = stepTwoState.value[idx];
    const cloned = JSON.parse(JSON.stringify(original));

    // 移除 id，避免衝突
    if (cloned.id) {
        delete cloned.id;
    }

    // 移除答案相關欄位
    if (cloned.correct_answers !== undefined) {
        delete cloned.correct_answers;
    }
    if (cloned.correct_answer !== undefined) {
        delete cloned.correct_answer;
    }

    // 移除分數相關欄位
    if (cloned.score !== undefined) {
        delete cloned.score;
    }
    if (cloned.question_score !== undefined) {
        delete cloned.question_score;
    }
    if (cloned.max_score !== undefined) {
        delete cloned.max_score;
    }

    // 移除選項中的 id 和分數
    if (cloned.options) {
        cloned.options = cloned.options.map((opt) => {
            const newOpt = { ...opt };
            if (newOpt.id) {
                delete newOpt.id;
            }
            if (newOpt.score !== undefined) {
                delete newOpt.score;
            }
            return newOpt;
        });
    }

    stepTwoState.value.splice(idx + 1, 0, cloned);

    // 重新初始化排序
    nextTick(() => {
        initSortable();
    });

    message.success('已複製題目');
};

// 刪除題目
const deleteQuestion = (idx) => {
    Modal.confirm({
        title: '確認刪除',
        content: '確定要刪除這個題目嗎？',
        okText: '確認',
        cancelText: '取消',
        okType: 'primary',
        cancelButtonProps: {
            style: {
                background: 'rgba(255, 255, 255, 0.85)',
                border: '1px solid rgba(168, 255, 246, 0.6)',
                borderRadius: '9999px',
                fontFamily: "'Courier New', monospace"
            }
        },
        okButtonProps: {
            style: {
                background: 'transparent',
                border: '2px solid #2a4a7a',
                borderRadius: '9999px',
                fontFamily: "'Courier New', monospace",
                color: '#1a1a2a'
            }
        },
        class: 'delete-question-modal',
        onOk: async () => {
            // 如果是編輯模式且有 id，需要調用 API 刪除
            const question = stepTwoState.value[idx];
            if (id && question?.id) {
                try {
                    await deleteQuestionAPI(id, question.id);
                } catch (error) {
                    const { response } = error;
                    const { data: d } = response || {};
                    const { message: m } = d || {};
                    showErrorMessage(m || '刪除題目失敗');
                    return; // 如果 API 調用失敗，不繼續刪除本地狀態
                }
            }

            // 先清理所有排序實例，避免索引錯誤
            // 清空所有排序實例和容器引用
            Object.keys(optionSortableInstances.value).forEach((key) => {
                const instance = optionSortableInstances.value[key];
                if (
                    instance &&
                    instance !== null &&
                    typeof instance.destroy === 'function'
                ) {
                    try {
                        instance.destroy();
                    } catch (error) {
                        // 忽略銷毀錯誤，可能實例已經被清理
                        console.warn('銷毀排序實例時發生錯誤:', error);
                    }
                }
            });

            // 清空題目排序實例
            if (sortableInstance && sortableInstance !== null) {
                try {
                    if (typeof sortableInstance.destroy === 'function') {
                        sortableInstance.destroy();
                    }
                } catch (error) {
                    console.warn('銷毀題目排序實例時發生錯誤:', error);
                }
                sortableInstance = null;
            }

            // 從數組中移除
            stepTwoState.value.splice(idx, 1);

            // 重置所有引用
            optionSortableInstances.value = {};
            optionContainerRefs.value = {};

            // 重新初始化排序
            nextTick(() => {
                // 重新初始化題目排序
                initSortable();

                // 重新初始化所有剩餘題目的選項排序
                stepTwoState.value.forEach((_, newIdx) => {
                    nextTick(() => {
                        initOptionSortable(newIdx);
                    });
                });
            });

            message.success('已刪除題目');
        }
    });
};

const createNewOption = (i) => {
    if (stepTwoState.value[i]) {
        stepTwoState.value[i]['options'].push({
            label: ''
        });
        // 重新初始化選項排序
        nextTick(() => {
            initOptionSortable(i);
        });
    }
};

const handleQuestionTypeChange = (e) => {
    const { question_type } = e;
    const questionIdx = stepTwoState.value.findIndex((q) => q === e);

    // 多維度模式只能使用單選題
    if (
        stepOneState.value.scoring_mode === 'dimensions' &&
        question_type !== 'single'
    ) {
        e.question_type = 'single';
        message.warning('多維度模式只能使用單選題');
    }

    switch (e.question_type) {
        case 'single':
        case 'multiple':
            // 只有一個選項
            e.options = [
                {
                    label: ''
                }
            ];
            // 重新初始化選項排序
            nextTick(() => {
                if (questionIdx !== -1) {
                    initOptionSortable(questionIdx);
                }
            });
            break;
        case 'text':
        case 'number':
            delete e.options;
            break;
    }
};

// 清理 API 請求數據
const prepareApiData = (data) => {
    const apiData = { ...data };

    // 如果 max_attempts_per_user 為 0，則不發送此參數
    if (
        apiData.max_attempts_per_user === 0 ||
        apiData.max_attempts_per_user === null
    ) {
        delete apiData.max_attempts_per_user;
    }

    return apiData;
};

const saveOneStep = async () => {
    try {
        await stepOneRef.value.validate();

        try {
            loading.value = true;

            const apiData = prepareApiData(stepOneState.value);
            let response = null;

            // 編輯模式：更新測驗
            if (id) {
                response = await updateQuizzes(id, apiData);
            } else if (stepOneState.value.id) {
                // 新增流程但已有 id（曾點過下一步又返回再下一步）：更新既有測驗，不重複建立
                response = await updateQuizzes(stepOneState.value.id, apiData);
            } else {
                response = await createQuizzes(apiData);
            }

            if (response.result) {
                stepOneState.value = response.data;
                message.success('已儲存');
                if (stepOneState.value.scoring_mode === 'dimensions') {
                    currentStep.value = 1;
                    if (id) {
                        await loadDimensions();
                    }
                } else {
                    currentStep.value = 1;
                }
            } else {
                showErrorMessage(response.message || '儲存失敗');
            }
        } catch (error) {
            console.error(`${id ? '編輯' : '新增'}測驗失敗:`, error);
            showErrorMessage('儲存失敗');
        } finally {
            loading.value = false;
        }
    } catch (error) {
        console.error('Validation failed:', error);
    }
};

const saveTwoSubmit = () => {
    let promises = [];

    stepTwoRef.value.forEach((s) => {
        promises.push(s.validate());
    });

    Promise.all(promises)
        .then((e) => {
            // 在送出 API 前先檢查單選和多選題的選項數量
            const invalidQuestions = [];

            stepTwoState.value.forEach((question, index) => {
                if (
                    question.question_type === 'single' ||
                    question.question_type === 'multiple'
                ) {
                    // 檢查選項數量
                    if (!question.options || question.options.length < 2) {
                        invalidQuestions.push({
                            index: index + 1,
                            type:
                                question.question_type === 'single'
                                    ? 'single'
                                    : 'multiple',
                            typeDisplay:
                                question.question_type === 'single'
                                    ? '單選'
                                    : '多選'
                        });
                    } else {
                        // 檢查選項是否有空的 label
                        const validOptions = question.options.filter(
                            (opt) => opt.label && opt.label.trim() !== ''
                        );
                        if (validOptions.length < 2) {
                            invalidQuestions.push({
                                index: index + 1,
                                type:
                                    question.question_type === 'single'
                                        ? 'single'
                                        : 'multiple',
                                typeDisplay:
                                    question.question_type === 'single'
                                        ? '單選'
                                        : '多選'
                            });
                        }
                    }
                }
            });

            // 如果有不符合條件的題目，顯示錯誤訊息並阻止提交
            if (invalidQuestions.length > 0) {
                const errorMessages = invalidQuestions.map((item) => {
                    return `第 ${item.index} 個題目：類型「${item.typeDisplay}」至少需要 2 個選項`;
                });
                showErrorMessage(errorMessages.join('；'));
                return;
            }

            loading.value = true;

            // 如果是問卷模式，自動設定題目的計分模式為 manual（自動）
            if (stepOneState.value.scoring_mode === 'survey') {
                stepTwoState.value.forEach((two) => {
                    two.scoring_mode = 'manual';
                });
            }

            // 如果是維度模式，自動設定題目的計分模式為 auto（不需審核）
            if (stepOneState.value.scoring_mode === 'dimensions') {
                stepTwoState.value.forEach((two) => {
                    two.scoring_mode = 'auto';
                });
            }

            if (id) {
                let promiseArr = [];

                stepTwoState.value.forEach((two) => {
                    if (two.id) {
                        promiseArr.push(updateQuestion(id, two.id, two));
                    } else {
                        promiseArr.push(createQuestion(id, two));
                    }
                });

                Promise.all(promiseArr)
                    .then((res) => {
                        message.success(res[0]['message']);

                        // 編輯模式：進入完成步驟（維度模式是步驟四，其他是步驟二）
                        currentStep.value =
                            stepOneState.value.scoring_mode === 'dimensions'
                                ? 4
                                : 2;
                    })
                    .catch((err) => {
                        const { response } = err;
                        const { data: d } = response;
                        const { message: m } = d;

                        showErrorMessage(m || '儲存失敗');
                    })
                    .finally((e) => {
                        loading.value = false;
                    });
            } else {
                // 新增模式：使用 stepOneState.value.id（第一步創建測驗後獲取的 ID）
                const quizId = stepOneState.value.id || id;

                if (!quizId) {
                    showErrorMessage('請先完成基本資訊設置');
                    loading.value = false;
                    return;
                }

                // 若題目已有 id（曾點過下一步後又返回再下一步），改為逐筆更新/新增，避免重複建立
                const hasExistingIds = stepTwoState.value.some((q) => q.id);
                if (hasExistingIds) {
                    const promiseArr = [];
                    stepTwoState.value.forEach((two) => {
                        if (two.id) {
                            promiseArr.push(
                                updateQuestion(quizId, two.id, two)
                            );
                        } else {
                            promiseArr.push(createQuestion(quizId, two));
                        }
                    });
                    Promise.all(promiseArr)
                        .then((res) => {
                            message.success(res[0]?.['message'] || '已儲存');
                            currentStep.value =
                                stepOneState.value.scoring_mode === 'dimensions'
                                    ? 4
                                    : 2;
                        })
                        .catch((err) => {
                            const { response } = err;
                            const { data: d } = response || {};
                            const { message: m } = d || {};
                            showErrorMessage(m || '儲存失敗');
                        })
                        .finally(() => {
                            loading.value = false;
                        });
                } else {
                    createBatchQuestions(quizId, {
                        'questions': stepTwoState.value
                    })
                        .then((res) => {
                            // batch API 回傳 data.questions，每項為 { question: {...}, options: [...] }，需壓平為題目+選項
                            const raw =
                                res.data?.questions ??
                                res.data?.data ??
                                (Array.isArray(res.data) ? res.data : []);
                            const questions = raw.map((item) => {
                                let q;
                                if (item && item.question != null) {
                                    q = {
                                        ...item.question,
                                        options:
                                            item.options ??
                                            item.question?.options ??
                                            []
                                    };
                                } else {
                                    q = { ...item };
                                }
                                q.is_required = q.is_required ? '1' : '0';
                                if (
                                    (q.question_type === 'single' ||
                                        q.question_type === 'multiple') &&
                                    (!q.options || q.options.length === 0)
                                ) {
                                    q.options = [{ label: '' }];
                                }
                                return q;
                            });
                            stepTwoState.value = questions;
                            message.success('已儲存');
                            currentStep.value =
                                stepOneState.value.scoring_mode === 'dimensions'
                                    ? 4
                                    : 2;
                        })
                        .catch((err) => {
                            const { response } = err;
                            const { data: d } = response || {};
                            const { message: m } = d || {};
                            showErrorMessage(m || '儲存失敗');
                        })
                        .finally(() => {
                            loading.value = false;
                        });
                }
            }
        })
        .catch((err) => {
            console.error('Two Section Validation failed:', err);
        });
};

const changeStep = async (idx) => {
    if (!stepOneState.value.id) return;
    currentStep.value = idx;
    // 回到題目設定步驟時，從 API 載入題目（新增與編輯皆一致，避免畫面空或不同步）
    const questionStepIdx =
        stepOneState.value.scoring_mode === 'dimensions' ? 3 : 1;
    if (idx === questionStepIdx) {
        await loadSingleQuiz();
    }
};

// 返回按鈕處理
const handleBack = () => {
    // 直接返回測驗設置頁面
    goList();
};

// 初始化拖動排序（題目）
const initSortable = () => {
    if (sortableInstance) {
        sortableInstance.destroy();
    }

    nextTick(() => {
        const container = questionsContainerRef.value;
        if (!container) return;

        sortableInstance = Sortable.create(container, {
            animation: 150,
            ghostClass: 'sortable-ghost',
            handle: '.drag-handle',
            onEnd: (evt) => {
                const { oldIndex, newIndex } = evt;

                if (oldIndex === newIndex) return;

                // 先恢復 DOM 順序（恢復到拖動前的狀態）
                evt.from.insertBefore(evt.item, evt.from.children[oldIndex]);

                // 移動數組元素
                const movedItem = stepTwoState.value.splice(oldIndex, 1)[0];
                stepTwoState.value.splice(newIndex, 0, movedItem);

                // 強制 Vue 更新視圖並重新初始化排序
                nextTick(() => {
                    // 重新初始化排序實例
                    initSortable();
                });
            }
        });
    });
};

// 初始化選項排序
const initOptionSortable = (questionIdx) => {
    // 先銷毀舊實例（安全銷毀）
    if (optionSortableInstances.value[questionIdx]) {
        try {
            const instance = optionSortableInstances.value[questionIdx];
            if (instance && typeof instance.destroy === 'function') {
                instance.destroy();
            }
        } catch (error) {
            console.warn('銷毀選項排序實例時發生錯誤:', error);
        }
        // 清除實例引用
        delete optionSortableInstances.value[questionIdx];
    }

    nextTick(() => {
        const container = optionContainerRefs.value[questionIdx];
        if (!container) return;

        const question = stepTwoState.value[questionIdx];
        if (!question || !question.options) return;

        try {
            optionSortableInstances.value[questionIdx] = Sortable.create(
                container,
                {
                    animation: 150,
                    ghostClass: 'sortable-ghost-option',
                    handle: '.option-drag-handle',
                    onEnd: (evt) => {
                        const { oldIndex, newIndex } = evt;

                        if (oldIndex === newIndex) return;

                        // 先恢復 DOM 順序（恢復到拖動前的狀態）
                        evt.from.insertBefore(
                            evt.item,
                            evt.from.children[oldIndex]
                        );

                        // 移動選項數組元素
                        const movedOption = question.options.splice(
                            oldIndex,
                            1
                        )[0];
                        question.options.splice(newIndex, 0, movedOption);

                        // 強制 Vue 更新視圖並重新初始化排序
                        nextTick(() => {
                            // 重新初始化排序實例
                            initOptionSortable(questionIdx);
                        });
                    }
                }
            );
        } catch (error) {
            console.error('初始化選項排序失敗:', error);
        }
    });
};

const loadSingleQuiz = async () => {
    const quizId = id || stepOneState.value.id;
    if (!quizId) return;

    try {
        loading.value = true;

        const response = await getSingleQuiz(quizId, 'answers');

        if (response.result) {
            const data = response.data;

            // 確保數字類型的欄位是數字而非字符串
            stepOneState.value = {
                ...data,
                passing_score:
                    data.passing_score !== null &&
                    data.passing_score !== undefined
                        ? Number(data.passing_score)
                        : null,
                time_limit_seconds:
                    data.time_limit_seconds !== null &&
                    data.time_limit_seconds !== undefined
                        ? Number(data.time_limit_seconds)
                        : null,
                max_attempts_per_user:
                    data.max_attempts_per_user !== null &&
                    data.max_attempts_per_user !== undefined
                        ? Number(data.max_attempts_per_user)
                        : 0
            };

            const questions = response.data.questions || [];
            questions.forEach((q) => {
                q.is_required = q.is_required ? '1' : '0';

                // 如果是維度模式，自動設定為不需審核（auto）
                if (data.scoring_mode === 'dimensions') {
                    q.scoring_mode = 'auto';
                }

                // 確保選項數量正確（至少一個）
                if (
                    q.question_type === 'single' ||
                    q.question_type === 'multiple'
                ) {
                    if (!q.options || q.options.length === 0) {
                        q.options = [{ label: '' }];
                    }
                }
            });

            stepTwoState.value = questions.filter(Boolean);

            // 維度模式：同步 dimensions 與結果卡，避免切回步驟時 resultsStateList[idx] 為 undefined
            if (data.scoring_mode === 'dimensions') {
                if (
                    Array.isArray(data.dimensions) &&
                    data.dimensions.length > 0
                ) {
                    const loadedDimensions = data.dimensions.map((d) => ({
                        id: d.id,
                        name: d.name || '',
                        description: d.description || ''
                    }));
                    dimensionsState.value = loadedDimensions;
                    originalDimensions.value = loadedDimensions.map((d) => ({
                        id: d.id,
                        name: d.name || '',
                        description: d.description || ''
                    }));
                } else {
                    await loadDimensions();
                }
                if (id) {
                    nextTick(async () => {
                        await loadResults();
                    });
                } else {
                    initResultsList();
                }
            }

            // 初始化排序
            nextTick(() => {
                initSortable();
                // 初始化每個題目的選項排序
                stepTwoState.value.forEach((q, idx) => {
                    if (
                        q.question_type === 'single' ||
                        q.question_type === 'multiple'
                    ) {
                        nextTick(() => {
                            initOptionSortable(idx);
                        });
                    }
                });
            });
        } else {
            showErrorMessage(response.message || '載入失敗');
        }
    } catch (error) {
        showErrorMessage('載入測驗清單失敗');
    } finally {
        loading.value = false;
    }
};

// 保存載入時的原始維度列表，用於判斷新增/更新/刪除
const originalDimensions = ref([]);

// 載入維度數據
const loadDimensions = async () => {
    try {
        const quizId = id || stepOneState.value.id;
        if (!quizId) return;

        const response = await getSingleQuiz(quizId, 'answers');
        if (response.result && response.data && response.data.dimensions) {
            const loadedDimensions = response.data.dimensions.map((d) => ({
                id: d.id,
                name: d.name || '',
                description: d.description || ''
            }));
            dimensionsState.value = loadedDimensions;
            // 保存原始列表，用於後續比較
            originalDimensions.value = loadedDimensions.map((d) => ({
                id: d.id,
                name: d.name || '',
                description: d.description || ''
            }));
        } else {
            dimensionsState.value = [{ name: '', description: '' }];
            originalDimensions.value = [];
        }
    } catch (error) {
        console.error('載入維度數據失敗:', error);
        dimensionsState.value = [{ name: '', description: '' }];
        originalDimensions.value = [];
    }
};

// 新增維度
const createNewDimension = () => {
    dimensionsState.value.push({
        id: null, // 新維度沒有 ID
        name: '',
        description: ''
    });
};

// 刪除維度
const deleteDimensionHandler = (idx) => {
    // 至少保留一個維度
    if (dimensionsState.value.length <= 1) {
        showErrorMessage('至少需要保留一個維度');
        return;
    }

    const dimension = dimensionsState.value[idx];

    Modal.confirm({
        title: '確認刪除',
        content: '確定要刪除此維度嗎？',
        okText: '確定',
        cancelText: '取消',
        okType: 'danger',
        onOk: async () => {
            // 如果有 id，調用 API 刪除
            if (dimension?.id) {
                try {
                    await deleteDimension(dimension.id);
                    message.success('刪除維度成功');
                } catch (error) {
                    const { response } = error;
                    const { data: d } = response || {};
                    const { message: m } = d || {};
                    showErrorMessage(m || '刪除維度失敗');
                    return;
                }
            }

            // 從本地狀態移除
            dimensionsState.value.splice(idx, 1);
        }
    });
};

// 保存維度
const saveDimensions = async () => {
    // 驗證維度數據
    const invalidDimensions = dimensionsState.value.filter(
        (d) => !d.name || d.name.trim() === ''
    );

    if (invalidDimensions.length > 0) {
        showErrorMessage('請填寫所有維度的名稱');
        return;
    }

    try {
        loading.value = true;
        const quizId = id || stepOneState.value.id;

        if (!quizId) {
            showErrorMessage('請先完成基本資訊設置');
            loading.value = false;
            return;
        }

        // 過濾掉空名稱的維度
        const validDimensions = dimensionsState.value.filter(
            (d) => d.name && d.name.trim() !== ''
        );

        // 找出需要刪除的維度（存在於原始列表但不在當前列表中的）
        const currentDimensionIds = new Set(
            validDimensions
                .map((d) => d.id)
                .filter((id) => id !== null && id !== undefined)
        );
        const toDelete = originalDimensions.value.filter(
            (d) => d.id && !currentDimensionIds.has(d.id)
        );

        // 刪除已移除的維度
        for (const dimension of toDelete) {
            if (dimension.id) {
                try {
                    await deleteDimension(dimension.id);
                } catch (error) {
                    console.error(`刪除維度 ${dimension.id} 失敗:`, error);
                }
            }
        }

        // 分類：需要更新的維度（有 ID）和需要新增的維度（沒有 ID）
        const toUpdate = validDimensions.filter(
            (d) => d.id !== null && d.id !== undefined
        );
        const toCreate = validDimensions.filter(
            (d) => !d.id || d.id === null || d.id === undefined
        );

        // 更新已存在的維度
        for (const dimension of toUpdate) {
            try {
                const updateData = {
                    name: dimension.name.trim()
                };
                // description 為可選，只有當有值時才添加
                if (dimension.description && dimension.description.trim()) {
                    updateData.description = dimension.description.trim();
                }
                await updateDimension(dimension.id, updateData);
            } catch (error) {
                console.error(`更新維度 ${dimension.id} 失敗:`, error);
                throw error;
            }
        }

        // 批量創建新的維度
        if (toCreate.length > 0) {
            const dimensionsData = toCreate.map((d) => ({
                name: d.name.trim(),
                description: d.description ? d.description.trim() : null
            }));

            await createDimensions(quizId, {
                dimensions: dimensionsData
            });
        }

        message.success('維度設置成功');

        // 重新載入維度數據以確保有 ID
        await loadDimensions();

        // 如果是編輯模式，載入結果卡；否則初始化空列表
        if (id) {
            await loadResults();
        } else {
            initResultsList();
        }

        // 進入下一步（結果卡設置）
        currentStep.value = 2;
    } catch (error) {
        const { response } = error;
        const { data: d } = response || {};
        const { message: m } = d || {};
        showErrorMessage(m || '保存維度失敗');
    } finally {
        loading.value = false;
    }
};

// 返回上一步
const goBackToStep = (step) => {
    currentStep.value = step;
};

const delOpt = (arr, idx, type) => {
    let warning_txt = type === 'multiple' ? 2 : 1;

    if (arr.length <= warning_txt) {
        showErrorMessage(`最少${warning_txt}個選項`);
        return;
    } else {
        arr.splice(idx, 1);
    }
};

const goList = (id) => {
    router.push({
        name: 'QuizzesManagement'
    });
};

// 發布按鈕處理
const publishing = ref(false);
// 儲存草稿處理
const savingDraft = ref(false);
// 非問卷模式下是否可以發布
const canPublishNonSurvey = ref(false);
// 檢查發布狀態中
const checkingPublishStatus = ref(false);

// 獲取測驗 ID（編輯模式用路由參數，新增模式用 stepOneState.value.id）
const getQuizId = () => {
    return id || stepOneState.value.id;
};

// 檢查發布狀態（僅限非問卷模式）
const checkPublishStatus = async () => {
    const quizId = getQuizId();
    if (!quizId || stepOneState.value.scoring_mode === 'survey') {
        return;
    }

    try {
        checkingPublishStatus.value = true;
        const report = await getPublishReport(quizId);
        // 根據 API 返回的報告判斷是否可以發布
        // 假設報告中有 can_publish 或 has_answer 等字段
        canPublishNonSurvey.value =
            report?.data?.can_publish ||
            report?.can_publish ||
            report?.data?.has_answer ||
            report?.has_answer ||
            false;
    } catch (error) {
        console.error('檢查發布狀態失敗:', error);
        canPublishNonSurvey.value = false;
    } finally {
        checkingPublishStatus.value = false;
    }
};

// 發布按鈕處理
const handlePublish = async () => {
    const quizId = getQuizId();
    if (!quizId) {
        showErrorMessage('測驗 ID 不存在');
        return;
    }

    try {
        publishing.value = true;

        // 如果是非問卷模式，再次確認可以發布
        if (stepOneState.value.scoring_mode !== 'survey') {
            const report = await getPublishReport(quizId);
            const canPublish =
                report?.data?.can_publish ||
                report?.can_publish ||
                report?.data?.has_answer ||
                report?.has_answer ||
                false;
            if (!canPublish) {
                showErrorMessage(
                    `測驗尚未設置${
                        stepOneState.value.scoring_mode === 'dimensions'
                            ? '維度'
                            : '答案'
                    }，無法發布`
                );
                await checkPublishStatus(); // 重新檢查狀態
                return;
            }
        }

        // 更新狀態為發布
        await updateQuizzes(quizId, { status: 'published' });
        message.success('測驗已發布');
        goList();
    } catch (error) {
        const { response } = error;
        const { data: d } = response || {};
        const { message: m } = d || {};
        showErrorMessage(m || '發布失敗');
    } finally {
        publishing.value = false;
    }
};

// 儲存草稿處理
const handleSaveDraft = async () => {
    const quizId = getQuizId();
    if (!quizId) {
        showErrorMessage('測驗 ID 不存在');
        return;
    }

    try {
        savingDraft.value = true;
        await updateQuizzes(quizId, { status: 'draft' });
        message.success('已儲存為草稿');
        goList();
    } catch (error) {
        const { response } = error;
        const { data: d } = response || {};
        const { message: m } = d || {};
        showErrorMessage(m || '儲存失敗');
    } finally {
        savingDraft.value = false;
    }
};

// 初始化結果卡列表（根據維度數量）
const initResultsList = () => {
    resultsStateList.value = [];
    const validDims = validDimensions.value;

    // 確保每個維度都有對應的結果卡
    validDims.forEach((dimension) => {
        // 檢查是否已有對應維度的結果卡
        const existingResult = resultsStateList.value.find(
            (r) => r.target_dimension_id === dimension.id
        );

        if (existingResult) {
            // 如果已有，保持現有數據
            return;
        } else {
            // 如果沒有，創建新的結果卡數據
            resultsStateList.value.push({
                id: null,
                title: '',
                subtitle: '',
                summary: '',
                imageFileList: [],
                select_mode: 'top_dimension',
                target_dimension_id: dimension.id
            });
        }
    });
};

// 保存結果卡
const saveResults = async () => {
    try {
        // 驗證所有表單
        const validationPromises = resultFormRefs.value
            .filter((ref) => ref)
            .map((ref) => ref.validate());

        await Promise.all(validationPromises);

        // 檢查是否所有維度都有標題
        const invalidResults = resultsStateList.value.filter(
            (result) => !result.title || result.title.trim() === ''
        );

        if (invalidResults.length > 0) {
            showErrorMessage('請為所有維度填寫結果卡標題');
            return;
        }

        try {
            loading.value = true;
            const quizId = id || stepOneState.value.id;

            if (!quizId) {
                showErrorMessage('請先完成基本資訊設置');
                loading.value = false;
                return;
            }

            // 獲取現有結果卡列表（編輯模式）
            let existingResultsMap = new Map();
            if (id) {
                try {
                    const resultsResponse = await getQuizResults(quizId);
                    if (
                        resultsResponse.result &&
                        resultsResponse.data &&
                        Array.isArray(resultsResponse.data)
                    ) {
                        resultsResponse.data.forEach((result) => {
                            existingResultsMap.set(
                                result.target_dimension_id,
                                result
                            );
                        });
                    }
                } catch (error) {
                    console.error('獲取現有結果卡失敗:', error);
                }
            }

            // 批量保存結果卡
            const savePromises = resultsStateList.value.map(
                async (resultData) => {
                    // 優先使用 resultData.id，如果沒有再從 existingResultsMap 查找
                    let resultId = resultData.id;

                    // 如果 resultData 沒有 id，但 existingResultsMap 有對應的結果卡，使用它的 id
                    if (!resultId) {
                        const existingResult = existingResultsMap.get(
                            resultData.target_dimension_id
                        );
                        resultId = existingResult?.id;
                    }

                    // 構建 FormData
                    const formData = new FormData();
                    formData.append('quiz_id', quizId);
                    formData.append('title', resultData.title.trim());
                    formData.append('select_mode', 'top_dimension');
                    formData.append(
                        'target_dimension_id',
                        resultData.target_dimension_id
                    );

                    // 可選欄位
                    if (resultData.subtitle && resultData.subtitle.trim()) {
                        formData.append('subtitle', resultData.subtitle.trim());
                    }
                    if (resultData.summary && resultData.summary.trim()) {
                        formData.append('summary', resultData.summary.trim());
                    }

                    // 如果有圖片檔案，添加到 FormData
                    if (
                        resultData.imageFileList &&
                        resultData.imageFileList.length > 0 &&
                        resultData.imageFileList[0].originFileObj
                    ) {
                        formData.append(
                            'image',
                            resultData.imageFileList[0].originFileObj
                        );
                    }

                    if (resultId) {
                        // 更新現有結果卡
                        return await updateQuizResult(resultId, formData);
                    } else {
                        // 創建新結果卡
                        return await createQuizResult(formData);
                    }
                }
            );

            await Promise.all(savePromises);

            message.success('已儲存所有結果卡');
            // 進入下一步（題目設定）
            currentStep.value = 3;
        } catch (error) {
            console.error('儲存結果卡失敗:', error);
            const { response } = error;
            const { data: d } = response || {};
            const { message: m } = d || {};
            showErrorMessage(m || '儲存失敗');
        } finally {
            loading.value = false;
        }
    } catch (error) {
        console.error('Validation failed:', error);
        showErrorMessage('請填寫所有必填欄位');
    }
};

// 載入結果卡數據（編輯模式）
const loadResults = async () => {
    try {
        const quizId = id || stepOneState.value.id;
        if (!quizId) return;

        const response = await getQuizResults(quizId);
        // API 回應格式：response.data.data 是結果卡陣列
        const resultsData = response?.data?.data || response?.data || [];
        if (
            response.result &&
            Array.isArray(resultsData) &&
            resultsData.length > 0
        ) {
            // 將結果卡按維度 ID 映射
            const resultsMap = new Map();

            // 直接使用 API 返回的 image_url，不需要額外的 API 調用（參考商品預覽方式）
            resultsData.forEach((result) => {
                resultsMap.set(result.target_dimension_id, {
                    id: result.id,
                    title: result.title || '',
                    subtitle: result.subtitle || '',
                    summary: result.summary || '',
                    imageFileList: result.image_url
                        ? [
                              {
                                  uid: `result-${result.id}`,
                                  name: 'image',
                                  status: 'done',
                                  url: result.image_url
                              }
                          ]
                        : [],
                    select_mode: 'top_dimension',
                    target_dimension_id: result.target_dimension_id
                });
            });

            // 清空舊的結果卡列表
            resultsStateList.value = [];

            // 根據有效維度初始化結果卡列表
            validDimensions.value.forEach((dimension) => {
                const existingResult = resultsMap.get(dimension.id);
                if (existingResult) {
                    resultsStateList.value.push(existingResult);
                } else {
                    resultsStateList.value.push({
                        id: null,
                        title: '',
                        subtitle: '',
                        summary: '',
                        imageFileList: [],
                        select_mode: 'top_dimension',
                        target_dimension_id: dimension.id
                    });
                }
            });
        } else {
            // 如果沒有結果卡，初始化空列表
            initResultsList();
        }
    } catch (error) {
        console.error('載入結果卡數據失敗:', error);
        // 如果載入失敗，初始化空列表
        initResultsList();
    }
};

// 監聽步驟變化，進入完成步驟時檢查發布狀態
watch(currentStep, async (newStep) => {
    const finalStep = stepOneState.value.scoring_mode === 'dimensions' ? 4 : 2;
    if (newStep === finalStep) {
        checkPublishStatus();
    }

    // 如果是多維度模式且進入結果卡設置步驟（步驟2），載入結果卡
    if (
        stepOneState.value.scoring_mode === 'dimensions' &&
        newStep === 2 &&
        id
    ) {
        // 確保維度已載入
        if (validDimensions.value.length === 0) {
            await loadDimensions();
        }
        // 清空現有結果卡列表
        resultsStateList.value = [];
        // 載入結果卡
        await loadResults();
    }
});

// 前往設定答案
const goToSetAnswers = () => {
    const quizId = getQuizId();
    if (quizId) {
        router.push({
            name: 'QuizzesSetAnswers',
            params: { id: quizId }
        });
    }
};

onMounted(() => {
    if (id) {
        loadSingleQuiz();
    } else {
        // 新增模式下，初始化排序
        nextTick(() => {
            initSortable();
        });
    }
});

// 元件卸載時清理所有 blob URL
onBeforeUnmount(() => {
    resultsStateList.value.forEach((result) => {
        if (result.imageFileList && result.imageFileList.length > 0) {
            result.imageFileList.forEach((file) => {
                if (file.url && file.url.startsWith('blob:')) {
                    URL.revokeObjectURL(file.url);
                }
            });
        }
    });
});

// 監聽 stepTwoState 變化，重新初始化排序
watch(
    () => stepTwoState.value.length,
    () => {
        nextTick(() => {
            initSortable();
        });
    }
);
</script>

<style>
.quiz-edit-page {
    padding: 24px;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    min-height: 100vh;
}

.page-header {
    margin-bottom: 24px;
}

.page-header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.page-header h1 {
    font-size: 24px;
    font-weight: 700;
    margin: 0;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

.header-back-button {
    color: rgba(138, 155, 168, 0.7) !important;
    font-family: 'Courier New', monospace !important;
    transition: all 0.2s ease !important;
}

.header-back-button:hover {
    color: #2a4a7a !important;
}

/* 步驟容器 - 獨立區域 */
.steps-container {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(252, 248, 255, 0.9)
    );
    border: 2px solid transparent;
    border-radius: 20px;
    padding: 40px 60px;
    margin-bottom: 32px;
    position: relative;
    box-shadow:
        0 4px 24px rgba(42, 74, 122, 0.12),
        0 0 40px rgba(138, 175, 255, 0.08);
    overflow: hidden;
}

.steps-container::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 20px;
    padding: 2px;
    background: linear-gradient(135deg, #64748b, #2a4a7a, #3b6ea5, #0e7490);
    mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    mask-composite: exclude;
    -webkit-mask-composite: xor;
    z-index: 0;
}

.steps-container::after {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.4),
        transparent
    );
    animation: shimmer 3s infinite;
    z-index: 1;
}

@keyframes shimmer {
    0% {
        left: -100%;
    }

    100% {
        left: 100%;
    }
}

/* 表單內容區域 - 獨立卡片 */
.content {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.98),
        rgba(252, 248, 255, 0.95)
    );
    border: 2px solid transparent;
    border-radius: 20px;
    padding: 48px;
    min-height: 500px;
    position: relative;
    box-shadow:
        0 8px 32px rgba(42, 74, 122, 0.15),
        0 0 60px rgba(138, 175, 255, 0.1);
}

.content::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 20px;
    padding: 2px;
    background: linear-gradient(135deg, #64748b, #2a4a7a, #3b6ea5, #0e7490);
    mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    mask-composite: exclude;
    -webkit-mask-composite: xor;
    z-index: 0;
}

.step_one_section,
.step_two_section,
.step_three_section,
.step_results_section {
    margin: 0 auto;
    width: 100%;
    max-width: 600px;
    position: relative;
    z-index: 1;
}

.step_two_section {
    max-width: 800px;
}

.step_two_section .del_opt {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    cursor: pointer;
}

/* 題目容器 */
.questions-container {
    position: relative;
}

/* 題目卡片 */
.step_two_section_card {
    padding: 20px 24px;
    margin-bottom: 16px;
    background: linear-gradient(
        135deg,
        rgba(255, 254, 255, 0.9),
        rgba(253, 238, 242, 0.6)
    );
    border-radius: 12px;
    border: 1px solid rgba(42, 74, 122, 0.2);
    box-shadow: 0 2px 12px rgba(42, 74, 122, 0.1);
    transition: all 0.3s ease;
    position: relative;
}

.step_two_section_card:hover {
    border-color: rgba(42, 74, 122, 0.4);
    box-shadow: 0 6px 24px rgba(42, 74, 122, 0.15);
    transform: translateY(-2px);
}

/* 題目標題區域 */
.question-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid rgba(42, 74, 122, 0.25);
    gap: 16px;
}

.question-header-left {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 0 0 auto;
}

.question-header-middle {
    display: flex;
    align-items: center;
    gap: 20px;
    flex: 1;
    justify-content: flex-end;
    margin-right: 8px;
}

.header-field-item {
    display: flex;
    align-items: center;
    gap: 8px;
}

.header-field-label {
    font-family: 'Courier New', monospace;
    font-size: 12px;
    font-weight: 600;
    color: #2a2a3a;
    white-space: nowrap;
}

.header-radio-group {
    display: flex;
    align-items: center;
}

:deep(.header-radio-group .ant-radio-button-wrapper) {
    height: 24px;
    line-height: 22px;
    font-size: 12px;
    font-family: 'Courier New', monospace;
    padding: 0 12px;
}

:deep(.header-radio-group .ant-radio-wrapper) {
    font-size: 12px;
    font-family: 'Courier New', monospace;
}

:deep(.header-radio-group .ant-radio-wrapper + .ant-radio-wrapper) {
    margin-left: 12px;
}

:deep(.question-header-middle .ant-switch) {
    min-width: 44px;
    height: 22px;
}

:deep(.question-header-middle .ant-switch-small) {
    min-width: 40px;
    height: 20px;
}

:deep(.question-header-middle .ant-switch .ant-switch-handle) {
    width: 18px;
    height: 18px;
}

:deep(.question-header-middle .ant-switch-small .ant-switch-handle) {
    width: 16px;
    height: 16px;
}

.drag-handle {
    color: rgba(138, 155, 168, 0.6);
    font-size: 18px;
    cursor: move;
    transition: all 0.2s ease;
    padding: 4px;
}

.drag-handle:hover {
    color: #2a4a7a;
    transform: scale(1.1);
}

.question-header-actions {
    display: flex;
    gap: 8px;
}

.question-action-button {
    border: none !important;
    padding: 4px 8px !important;
    min-width: auto !important;
    width: auto !important;
    height: auto !important;
    transition: all 0.2s ease !important;
}

/* 複製按鈕 - 藍色 #3b6ea5 */
.duplicate-question-button,
.duplicate-question-button *,
:deep(.duplicate-question-button),
:deep(.duplicate-question-button *),
:deep(.duplicate-question-button .anticon),
:deep(.duplicate-question-button .ant-btn),
:deep(.duplicate-question-button span),
:deep(.duplicate-question-button svg),
:deep(.duplicate-question-button path) {
    color: #3b6ea5 !important;
    fill: #3b6ea5 !important;
}

.duplicate-question-button:hover,
.duplicate-question-button:hover *,
:deep(.duplicate-question-button:hover),
:deep(.duplicate-question-button:hover *),
:deep(.duplicate-question-button:hover .anticon),
:deep(.duplicate-question-button:hover span),
:deep(.duplicate-question-button:hover svg),
:deep(.duplicate-question-button:hover path) {
    color: #3b6ea5 !important;
    fill: #3b6ea5 !important;
    background: rgba(138, 175, 255, 0.15) !important;
    transform: scale(1.1);
}

/* 刪除按鈕 - 紅色 #FF7875 */
.delete-question-button,
.delete-question-button *,
:deep(.delete-question-button),
:deep(.delete-question-button *),
:deep(.delete-question-button .anticon),
:deep(.delete-question-button .ant-btn),
:deep(.delete-question-button span),
:deep(.delete-question-button svg),
:deep(.delete-question-button path) {
    color: #ff7875 !important;
    fill: #ff7875 !important;
}

.delete-question-button:hover,
.delete-question-button:hover *,
:deep(.delete-question-button:hover),
:deep(.delete-question-button:hover *),
:deep(.delete-question-button:hover .anticon),
:deep(.delete-question-button:hover span),
:deep(.delete-question-button:hover svg),
:deep(.delete-question-button:hover path) {
    color: #ff7875 !important;
    fill: #ff7875 !important;
    background: rgba(255, 120, 117, 0.15) !important;
    transform: scale(1.1);
}

/* 新增按鈕 - 紫色 #2a4a7a */
.add-question-inline-button,
.add-question-inline-button *,
:deep(.add-question-inline-button),
:deep(.add-question-inline-button *),
:deep(.add-question-inline-button .anticon),
:deep(.add-question-inline-button .ant-btn),
:deep(.add-question-inline-button span),
:deep(.add-question-inline-button svg),
:deep(.add-question-inline-button path) {
    color: #2a4a7a !important;
    fill: #2a4a7a !important;
}

.add-question-inline-button:hover,
.add-question-inline-button:hover *,
:deep(.add-question-inline-button:hover),
:deep(.add-question-inline-button:hover *),
:deep(.add-question-inline-button:hover .anticon),
:deep(.add-question-inline-button:hover span),
:deep(.add-question-inline-button:hover svg),
:deep(.add-question-inline-button:hover path) {
    color: #2a4a7a !important;
    fill: #2a4a7a !important;
    background: rgba(42, 74, 122, 0.15) !important;
    transform: scale(1.1);
}

:deep(.question-action-button .anticon) {
    font-size: 16px;
}

/* 空狀態樣式 */
.empty-questions-state {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
    padding: 60px 32px;
}

.empty-state-content {
    text-align: center;
    max-width: 400px;
}

.empty-state-icon {
    font-size: 64px;
    margin-bottom: 24px;
    opacity: 0.8;
    animation: float 3s ease-in-out infinite;
}

.empty-state-title {
    font-family: 'Courier New', monospace;
    font-size: 20px;
    font-weight: 700;
    color: #2a2a3a;
    margin-bottom: 12px;
}

.empty-state-description {
    font-family: 'Courier New', monospace;
    font-size: 14px;
    color: #8a9ba8;
    margin-bottom: 32px;
    line-height: 1.6;
}

.empty-state-button {
    background: linear-gradient(135deg, #2a4a7a, #3b6ea5) !important;
    border: none !important;
    color: #fff !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    border-radius: 10px !important;
    padding: 12px 32px !important;
    height: auto !important;
    box-shadow: 0 4px 12px rgba(42, 74, 122, 0.3) !important;
    transition: all 0.3s ease !important;
}

.empty-state-button:hover {
    background: linear-gradient(135deg, #1a4268, #3b6ea5) !important;
    transform: translateY(-2px) !important;
    box-shadow: 0 6px 20px rgba(42, 74, 122, 0.4) !important;
}

@keyframes float {
    0%,
    100% {
        transform: translateY(0px);
    }

    50% {
        transform: translateY(-10px);
    }
}

.question-title {
    font-family: 'Courier New', monospace;
    color: #2a4a7a;
    font-size: 20px;
    font-weight: 700;
    margin: 0;
    text-shadow: 0 0 6px rgba(42, 74, 122, 0.25);
    display: flex;
    align-items: center;
    gap: 8px;
}

.required-star {
    color: #ff4d4f;
    font-size: 18px;
    font-weight: 700;
    text-shadow: 0 0 4px rgba(255, 77, 79, 0.5);
}

/* 選項容器 */
.options-container {
    position: relative;
}

/* 選項樣式 */
.option-item {
    margin-bottom: 12px;
    position: relative;
    cursor: move;
}

.option-item:hover {
    cursor: grab;
}

.option-item:active {
    cursor: grabbing;
}

.option-form-item {
    margin-bottom: 0 !important;
}

/* 選項拖動手柄 */
.option-drag-handle {
    color: rgba(138, 155, 168, 0.5);
    font-size: 16px;
    cursor: move;
    transition: all 0.2s ease;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.option-drag-handle:hover {
    color: #2a4a7a;
    transform: scale(1.1);
}

.option-drag-col {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 !important;
}

/* 選項類型圖標（單選/多選） */
.option-icon-col {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 0 !important;
}

.option-type-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
}

/* 圓形單選圖標 */
.radio-circle {
    width: 18px;
    height: 18px;
    border: 2px solid rgba(138, 155, 168, 0.6);
    border-radius: 50%;
    background: transparent;
    transition: all 0.2s ease;
}

.radio-icon:hover .radio-circle {
    border-color: #2a4a7a;
}

/* 方形多選圖標 */
.checkbox-square {
    width: 18px;
    height: 18px;
    border: 2px solid rgba(138, 155, 168, 0.6);
    border-radius: 3px;
    background: transparent;
    transition: all 0.2s ease;
}

.checkbox-icon:hover .checkbox-square {
    border-color: #2a4a7a;
}

/* 選項編號 */
.option-number-col {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 0 !important;
}

.option-number {
    font-family: 'Courier New', monospace;
    font-weight: 600;
    color: #2a2a3a;
    font-size: 14px;
    display: inline-block;
    padding: 4px 0;
}

/* 選項輸入框 */
.option-input-col {
    padding-right: 0 !important;
}

.option-input {
    width: 100% !important;
}

.option-delete-icon {
    color: rgba(138, 155, 168, 0.6);
    cursor: pointer;
    transition: all 0.2s ease;
    padding: 2px;
}

.option-delete-icon:hover {
    color: #ff7875;
    transform: scale(1.1);
}

/* 新增選項圖標按鈕 */
.add-option-icon-button {
    color: #2a4a7a !important;
    border: none !important;
    padding: 4px !important;
    min-width: auto !important;
    width: auto !important;
    height: auto !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    transition: all 0.2s ease !important;
}

.add-option-icon-button:hover {
    color: #1a4268 !important;
    background: rgba(42, 74, 122, 0.1) !important;
    transform: scale(1.1);
}

.add-option-icon-button:active {
    transform: scale(0.95);
}

:deep(.add-option-icon-button .anticon) {
    font-size: 18px;
}

/* 新增選項按鈕文字樣式 */
.add-option-button-text {
    border-color: rgba(42, 74, 122, 0.6) !important;
    color: rgba(42, 74, 122, 0.9) !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    border-radius: 10px !important;
    transition: all 0.3s ease !important;
    height: 40px !important;
}

.add-option-button-text:hover {
    border-color: #2a4a7a !important;
    color: #2a4a7a !important;
    background: rgba(42, 74, 122, 0.05) !important;
    transform: translateY(-2px) !important;
}

.add-option-button-text .anticon {
    margin-right: 8px;
}

/* 拖動排序樣式 */
.sortable-ghost {
    opacity: 0.5;
    background: rgba(42, 74, 122, 0.1) !important;
}

.sortable-ghost-option {
    opacity: 0.5;
    background: rgba(42, 74, 122, 0.1) !important;
}

/* 新增選項按鈕行 */
.add-option-button-row {
    margin-top: 8px;
}

.question-card {
    cursor: move;
}

.question-card:hover {
    cursor: grab;
}

.question-card:active {
    cursor: grabbing;
}

/* 表單項目間距調整 - 更緊湊 */
.question-form .ant-form-item {
    margin-bottom: 12px;
}

.question-form .ant-form-item-label {
    padding-bottom: 4px;
}

.question-form .ant-form-item-label > label {
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    color: #2a2a3a !important;
    font-size: 13px !important;
}

/* Switch 樣式 */
:deep(.question-form .ant-switch) {
    font-family: 'Courier New', monospace !important;
}

:deep(.question-form .ant-switch-checked) {
    background-color: #2a4a7a !important;
}

:deep(.question-form .ant-switch-checked:hover:not(.ant-switch-disabled)) {
    background-color: #1a4268 !important;
}

/* 移除 Switch 按鈕周圍的方形邊框 */
:deep(.question-form .ant-switch .ant-switch-handle) {
    border: none !important;
    box-shadow: none !important;
    outline: none !important;
}

:deep(.question-form .ant-switch .ant-switch-handle::before) {
    border-radius: 50% !important;
    background-color: #fff !important;
    box-shadow: none !important;
}

:deep(.question-form .ant-switch:focus .ant-switch-handle),
:deep(.question-form .ant-switch:hover .ant-switch-handle) {
    outline: none !important;
    box-shadow: none !important;
}

/* Radio 樣式 */
:deep(
    .question-form
        .ant-radio-button-wrapper-checked:not(
            .ant-radio-button-wrapper-disabled
        )
),
:deep(.question-form .ant-radio-checked .ant-radio-inner) {
    border-color: #2a4a7a !important;
    background-color: #2a4a7a !important;
}

:deep(.question-form .ant-radio-checked .ant-radio-inner::after) {
    background-color: #fff !important;
}

:deep(.question-form .ant-radio:hover .ant-radio-inner),
:deep(.question-form .ant-radio-input:focus + .ant-radio-inner) {
    border-color: #2a4a7a !important;
}

/* 步驟樣式優化 - 簡潔設計 */
:deep(.steps-container .ant-steps) {
    position: relative;
    z-index: 2;
}

:deep(.steps-container .ant-steps-item) {
    padding: 0 16px;
}

:deep(.steps-container .ant-steps-item-process .ant-steps-item-icon) {
    background: linear-gradient(135deg, #2a4a7a, #3b6ea5) !important;
    border-color: #2a4a7a !important;
    box-shadow:
        0 0 16px rgba(42, 74, 122, 0.6),
        0 0 32px rgba(138, 175, 255, 0.3) !important;
    transform: scale(1.1);
    transition: all 0.3s ease;
}

:deep(.steps-container .ant-steps-item-wait .ant-steps-item-icon) {
    background: rgba(255, 255, 255, 0.8) !important;
    border-color: rgba(42, 74, 122, 0.3) !important;
}

:deep(.steps-container .ant-steps-item-finish .ant-steps-item-icon) {
    background: linear-gradient(135deg, #2a4a7a, #3b6ea5) !important;
    border-color: #2a4a7a !important;
    box-shadow: 0 0 12px rgba(42, 74, 122, 0.4);
}

:deep(.steps-container .ant-steps-item-finish .ant-steps-item-icon svg) {
    fill: #fff !important;
}

:deep(.steps-container .ant-steps-item-title) {
    font-family: 'Courier New', monospace !important;
    font-weight: 700 !important;
    color: #2a2a3a !important;
    font-size: 16px !important;
    margin-top: 8px !important;
    line-height: 1.4 !important;
}

:deep(.steps-container .ant-steps-item-process .ant-steps-item-title) {
    color: #2a4a7a !important;
    text-shadow: 0 0 8px rgba(42, 74, 122, 0.3);
}

:deep(.steps-container .ant-steps-item-description) {
    font-family: 'Courier New', monospace !important;
    color: #8a9ba8 !important;
    font-size: 12px !important;
    margin-top: 4px !important;
    font-weight: 400 !important;
    line-height: 1.3 !important;
}

:deep(.steps-container .ant-steps-item-process .ant-steps-item-description) {
    color: #3b6ea5 !important;
}

:deep(.steps-container .ant-steps-item-finish .ant-steps-item-description) {
    color: #8a9ba8 !important;
}

:deep(.steps-container .ant-steps-item-finish .ant-steps-item-title) {
    color: #2a4a7a !important;
}

/* 步驟連線優化 */
:deep(.steps-container .ant-steps-item-finish .ant-steps-item-tail::after) {
    background: linear-gradient(90deg, #2a4a7a, #3b6ea5) !important;
    height: 2px !important;
}

:deep(.steps-container .ant-steps-item-wait .ant-steps-item-tail::after) {
    background: rgba(42, 74, 122, 0.2) !important;
    height: 1px !important;
}

/* 按鈕樣式 */
.submit-button {
    background: linear-gradient(135deg, #2a4a7a, #3b6ea5) !important;
    border: none !important;
    color: #fff !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    border-radius: 10px !important;
    transition: all 0.3s ease !important;
    box-shadow: 0 4px 12px rgba(42, 74, 122, 0.3) !important;
}

.submit-button:hover {
    background: linear-gradient(135deg, #1a4268, #3b6ea5) !important;
    transform: translateY(-2px) !important;
    box-shadow: 0 6px 20px rgba(42, 74, 122, 0.4) !important;
}

/* 下一步按鈕樣式 */
.next-button {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 12px !important;
    position: relative !important;
    overflow: hidden !important;
}

.next-button span {
    font-size: 16px !important;
    font-weight: 700 !important;
    letter-spacing: 0.5px !important;
}

.next-icon {
    font-size: 18px !important;
    transition: transform 0.3s ease !important;
    display: inline-flex !important;
    align-items: center !important;
}

.next-button:hover .next-icon {
    transform: translateX(4px) !important;
}

.next-button:active {
    transform: translateY(-1px) scale(0.98) !important;
}

/* 下一步按鈕發光效果 */
.next-button::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.2),
        transparent
    );
    transition: left 0.5s ease;
}

.next-button:hover::before {
    left: 100%;
}

.add-question-button {
    border-color: rgba(42, 74, 122, 0.6) !important;
    color: rgba(42, 74, 122, 0.9) !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    border-radius: 10px !important;
    transition: all 0.3s ease !important;
}

.add-question-button:hover {
    border-color: #2a4a7a !important;
    color: #2a4a7a !important;
    background: rgba(42, 74, 122, 0.05) !important;
    transform: translateY(-2px) !important;
}

.back-button {
    background: #fff !important;
    border: 1px solid rgba(168, 255, 246, 0.6) !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    border-radius: 10px !important;
    transition: all 0.3s ease !important;
}

.back-button:hover {
    border-color: #64748b !important;
    background: rgba(255, 158, 207, 0.05) !important;
    transform: translateY(-2px) !important;
}

/* 表單樣式優化 */
.quiz-form {
    position: relative;
    z-index: 1;
}

.form-item-full {
    width: 100%;
}

.submit-item {
    margin-top: 32px;
    padding-top: 24px;
    border-top: 1px solid rgba(42, 74, 122, 0.15);
}

:deep(.step_one_section .ant-form-item) {
    margin-bottom: 24px;
}

:deep(.step_one_section .ant-form-item-label) {
    padding-bottom: 8px;
}

:deep(.step_one_section .ant-form-item-label > label) {
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    color: #2a2a3a !important;
    font-size: 14px !important;
}

:deep(.step_two_section_card .ant-form-item-label > label) {
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    color: #2a2a3a !important;
}

/* 輸入框樣式 */
:deep(.ant-input),
:deep(.ant-select-selector),
:deep(.ant-input-number),
:deep(.ant-input-number-input),
:deep(.ant-input) {
    border-radius: 10px !important;
    border-color: rgba(42, 74, 122, 0.3) !important;
    transition: all 0.3s ease !important;
    font-family: 'Courier New', monospace !important;
}

:deep(.ant-input:hover),
:deep(.ant-select-selector:hover),
:deep(.ant-input-number:hover),
:deep(.ant-input:hover) {
    border-color: rgba(42, 74, 122, 0.6) !important;
    box-shadow: 0 0 0 2px rgba(42, 74, 122, 0.1) !important;
}

:deep(.ant-input:focus),
:deep(.ant-input-focused),
:deep(.ant-select-focused .ant-select-selector),
:deep(.ant-input-number-focused),
:deep(.ant-input:focus) {
    border-color: #2a4a7a !important;
    box-shadow:
        0 0 0 2px rgba(42, 74, 122, 0.2),
        0 4px 12px rgba(42, 74, 122, 0.15) !important;
}

/* Textarea 樣式 */
:deep(.ant-input) {
    resize: vertical;
    min-height: 100px;
}

:deep(.ant-input-count) {
    font-family: 'Courier New', monospace !important;
    color: rgba(138, 155, 168, 0.8) !important;
    font-size: 12px !important;
}

/* 幫助文字樣式 */
:deep(.ant-form-item-explain) {
    font-family: 'Courier New', monospace !important;
    font-size: 12px !important;
    color: rgba(138, 155, 168, 0.8) !important;
    line-height: 1.5 !important;
    margin-top: 4px !important;
}

:deep(.ant-form-item-explain-error) {
    color: #ff7875 !important;
}

:deep(.ant-input-number) {
    width: 100% !important;
}

:deep(.ant-input-number .ant-input-number-input) {
    border-radius: 10px !important;
}

/* 下拉選單樣式 */
:deep(.ant-select-dropdown) {
    border-radius: 10px !important;
    border: 1px solid rgba(42, 74, 122, 0.3) !important;
    box-shadow: 0 4px 16px rgba(42, 74, 122, 0.15) !important;
}

:deep(.ant-select-item) {
    font-family: 'Courier New', monospace !important;
    transition: all 0.2s ease !important;
}

:deep(.ant-select-item:hover),
:deep(.ant-select-item-option-selected:not(.ant-select-item-option-disabled)) {
    background: rgba(42, 74, 122, 0.1) !important;
}

:deep(.ant-select-item-option-selected:not(.ant-select-item-option-disabled)) {
    color: #2a4a7a !important;
    font-weight: 600 !important;
}

/* 表單行樣式 */
:deep(.ant-row) {
    margin-left: 0 !important;
    margin-right: 0 !important;
}

:deep(.ant-col) {
    padding-left: 0 !important;
    padding-right: 0 !important;
}

:deep(.ant-row .ant-col:first-child) {
    padding-right: 12px !important;
}

:deep(.ant-row .ant-col:last-child) {
    padding-left: 12px !important;
}

/* Placeholder 樣式 */
:deep(.ant-input::placeholder),
:deep(.ant-input-number-input::placeholder) {
    color: rgba(138, 155, 168, 0.6) !important;
    font-family: 'Courier New', monospace !important;
}

/* 響應式設計 */
@media (max-width: 768px) {
    :deep(.ant-row .ant-col) {
        padding-left: 0 !important;
        padding-right: 0 !important;
    }

    :deep(.ant-col) {
        margin-bottom: 16px;
    }
}

/* 第三步樣式 */
.step-three-content {
    padding: 48px 32px;
    text-align: center;
    max-width: 600px;
    margin: 0 auto;
}

.publish-info {
    margin-bottom: 40px;
}

.publish-alert {
    border-radius: 16px !important;
    border: none !important;
    box-shadow:
        0 8px 24px rgba(42, 74, 122, 0.15),
        0 0 0 1px rgba(42, 74, 122, 0.1) !important;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.95)
    ) !important;
    backdrop-filter: blur(10px) !important;
    padding: 24px !important;
}

/* 警告類型 Alert - 使用蒸氣波色系 */
.publish-alert :deep(.ant-alert-warning) {
    background: linear-gradient(
        135deg,
        rgba(255, 168, 122, 0.1),
        rgba(255, 158, 207, 0.05)
    ) !important;
    border: 1px solid rgba(255, 168, 122, 0.3) !important;
}

.publish-alert :deep(.ant-alert-warning .ant-alert-icon) {
    color: #ffa87a !important;
}

.publish-alert :deep(.ant-alert-warning .ant-alert-message) {
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    font-size: 18px !important;
}

.publish-alert :deep(.ant-alert-warning .ant-alert-description) {
    color: rgba(42, 42, 58, 0.8) !important;
    font-family: 'Courier New', monospace !important;
    font-size: 14px !important;
    line-height: 1.6 !important;
    margin-top: 8px !important;
}

/* 成功類型 Alert */
.publish-alert :deep(.ant-alert-success) {
    background: linear-gradient(
        135deg,
        rgba(168, 255, 246, 0.1),
        rgba(138, 175, 255, 0.05)
    ) !important;
    border: 1px solid rgba(168, 255, 246, 0.3) !important;
}

.publish-alert :deep(.ant-alert-success .ant-alert-icon) {
    color: #0e7490 !important;
}

.publish-alert :deep(.ant-alert-success .ant-alert-message) {
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    font-size: 18px !important;
}

.publish-alert :deep(.ant-alert-success .ant-alert-description) {
    color: rgba(42, 42, 58, 0.8) !important;
    font-family: 'Courier New', monospace !important;
    font-size: 14px !important;
    line-height: 1.6 !important;
    margin-top: 8px !important;
}

/* 資訊類型 Alert */
.publish-alert :deep(.ant-alert-info) {
    background: linear-gradient(
        135deg,
        rgba(138, 175, 255, 0.1),
        rgba(42, 74, 122, 0.05)
    ) !important;
    border: 1px solid rgba(138, 175, 255, 0.3) !important;
}

.publish-alert :deep(.ant-alert-info .ant-alert-icon) {
    color: #3b6ea5 !important;
}

.publish-alert :deep(.ant-alert-info .ant-alert-message) {
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    font-size: 18px !important;
}

.publish-alert :deep(.ant-alert-info .ant-alert-description) {
    color: rgba(42, 42, 58, 0.8) !important;
    font-family: 'Courier New', monospace !important;
    font-size: 14px !important;
    line-height: 1.6 !important;
    margin-top: 8px !important;
}

/* 任務列表樣式 - 遊戲任務風格 */
.task-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 40px;
    align-items: center;
}

.task-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 24px;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 12px;
    border: 2px solid;
    min-width: 280px;
    font-family: 'Courier New', monospace;
    font-weight: 600;
    font-size: 16px;
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.task-item.task-completed {
    border-color: #52c41a;
    background: linear-gradient(
        135deg,
        rgba(82, 196, 26, 0.1),
        rgba(82, 196, 26, 0.05)
    );
    color: #2a2a3a;
}

.task-item.task-pending {
    border-color: #ff4d4f;
    background: linear-gradient(
        135deg,
        rgba(255, 77, 79, 0.1),
        rgba(255, 77, 79, 0.05)
    );
    color: #2a2a3a;
}

.task-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    font-size: 16px;
    font-weight: bold;
    flex-shrink: 0;
}

.task-completed .task-icon {
    background: #52c41a;
    color: #ffffff;
}

.task-pending .task-icon {
    background: #ff4d4f;
    color: #ffffff;
}

.task-text {
    flex: 1;
}

/* 說明文字樣式 */
.step-three-description {
    margin: 32px 0;
    padding: 24px;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    border-radius: 12px;
    border: 1px solid rgba(42, 74, 122, 0.2);
    box-shadow: 0 2px 12px rgba(42, 74, 122, 0.1);
}

.description-text {
    font-family: 'Courier New', monospace;
    font-size: 16px;
    font-weight: 600;
    color: #2a2a3a;
    margin: 0 0 12px 0;
    line-height: 1.6;
    text-align: center;
}

.description-text.success-text {
    color: #52c41a;
}

.description-text.warning-text {
    color: #ff4d4f;
}

.description-subtext {
    font-family: 'Courier New', monospace;
    font-size: 14px;
    color: rgba(42, 42, 58, 0.7);
    margin: 0;
    line-height: 1.6;
    text-align: center;
}

.checking-status {
    margin: 24px 0;
    padding: 12px 24px;
    background: rgba(138, 175, 255, 0.1);
    border-radius: 8px;
    color: rgba(138, 175, 255, 0.9);
    font-family: 'Courier New', monospace;
    font-size: 14px;
    text-align: center;
}

.step-three-actions {
    margin-top: 32px;
    display: flex;
    justify-content: center;
    gap: 16px;
}

/* 發布按鈕樣式 */
.publish-button {
    min-width: 120px;
    background: linear-gradient(135deg, #2a4a7a, #3b6ea5) !important;
    border: none !important;
    color: #ffffff !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    border-radius: 10px !important;
    box-shadow: 0 4px 16px rgba(42, 74, 122, 0.3) !important;
    transition: all 0.3s ease !important;
}

.publish-button:hover {
    background: linear-gradient(135deg, #3b6ea5, #2a4a7a) !important;
    box-shadow: 0 6px 20px rgba(42, 74, 122, 0.4) !important;
    transform: translateY(-2px) !important;
}

.publish-button:active {
    transform: translateY(0) !important;
}

/* 儲存草稿按鈕樣式 */
.save-draft-button {
    min-width: 120px;
    background: #ffffff !important;
    border: 1.5px solid #0e7490 !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    border-radius: 10px !important;
    transition: all 0.3s ease !important;
}

.save-draft-button:hover {
    background: rgba(168, 255, 246, 0.1) !important;
    border-color: #2a4a7a !important;
    color: #2a2a3a !important;
    box-shadow: 0 4px 12px rgba(168, 255, 246, 0.2) !important;
    transform: translateY(-2px) !important;
}

.save-draft-button:active {
    transform: translateY(0) !important;
}

/* 前往設定答案按鈕樣式 */
.go-to-answers-button {
    min-width: 140px;
    background: linear-gradient(135deg, #64748b, #ffa87a) !important;
    border: none !important;
    color: #ffffff !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    border-radius: 10px !important;
    box-shadow: 0 4px 16px rgba(255, 158, 207, 0.3) !important;
    transition: all 0.3s ease !important;
}

.go-to-answers-button:hover {
    background: linear-gradient(135deg, #ffa87a, #64748b) !important;
    box-shadow: 0 6px 20px rgba(255, 158, 207, 0.4) !important;
    transform: translateY(-2px) !important;
}

.go-to-answers-button:active {
    transform: translateY(0) !important;
}

/* 維度設置樣式 */
.step_dimensions_section {
    padding: 24px;
}

.dimensions-container {
    max-width: 900px;
    margin: 0 auto;
}

.dimensions-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 24px;
}

.dimension-item-wrapper {
    position: relative;
}

.dimension-card {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    border-radius: 12px;
    padding: 16px;
    padding-right: 48px;
    box-shadow:
        0 4px 16px rgba(42, 74, 122, 0.12),
        0 0 0 1px rgba(42, 74, 122, 0.1);
    backdrop-filter: blur(10px);
    transition: all 0.3s ease;
    position: relative;
}

.dimension-card:hover {
    box-shadow:
        0 8px 20px rgba(42, 74, 122, 0.18),
        0 0 0 1px rgba(42, 74, 122, 0.2);
    transform: translateY(-1px);
}

.delete-dimension-button {
    position: absolute;
    top: 12px;
    right: 12px;
    font-family: 'Courier New', monospace !important;
    color: rgba(255, 77, 79, 0.85) !important;
    transition: all 0.3s ease !important;
    z-index: 1;
}

.delete-dimension-button:hover {
    color: #ff4d4f !important;
    background: rgba(255, 77, 79, 0.05) !important;
}

.dimension-content {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.dimension-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 0;
}

.dimension-label {
    font-size: 13px;
    font-weight: 600;
    color: rgba(42, 42, 58, 0.8);
    font-family: 'Courier New', monospace;
    margin-bottom: 4px;
}

.add-dimension-wrapper {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px dashed rgba(42, 74, 122, 0.3);
}

.add-dimension-button {
    font-family: 'Courier New', monospace !important;
    font-weight: 500 !important;
    border-color: rgba(42, 74, 122, 0.5) !important;
    color: rgba(42, 74, 122, 0.9) !important;
    transition: all 0.3s ease !important;
    height: 44px !important;
}

.add-dimension-button:hover {
    border-color: rgba(42, 74, 122, 0.8) !important;
    color: rgba(42, 74, 122, 1) !important;
    background: rgba(42, 74, 122, 0.05) !important;
    transform: translateY(-1px) !important;
}

/* 結果卡設置樣式 */
.results-header {
    margin-bottom: 32px;
    padding-bottom: 20px;
    border-bottom: 2px solid rgba(42, 74, 122, 0.3);
}

.results-title {
    font-family: 'Courier New', monospace;
    font-size: 20px;
    font-weight: 700;
    color: #2a2a3a;
    margin: 0 0 12px 0;
}

.results-info {
    font-family: 'Courier New', monospace;
    font-size: 14px;
    color: #6a6a7a;
    display: flex;
    align-items: center;
    gap: 8px;
}

.dimension-count {
    font-weight: 700;
    color: #2a4a7a;
    font-size: 16px;
}

.results-list {
    display: flex;
    flex-direction: column;
    gap: 24px;
    margin-bottom: 32px;
}

.result-card-item {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    border-radius: 12px;
    padding: 24px;
    border: 1px solid rgba(42, 74, 122, 0.2);
    box-shadow: 0 2px 12px rgba(42, 74, 122, 0.1);
    transition: all 0.3s ease;
}

.result-card-item:hover {
    border-color: rgba(42, 74, 122, 0.4);
    box-shadow: 0 6px 24px rgba(42, 74, 122, 0.15);
    transform: translateY(-2px);
}

.result-card-header {
    margin-bottom: 20px;
    padding-bottom: 16px;
    border-bottom: 1px solid rgba(42, 74, 122, 0.2);
}

.result-card-dimension {
    display: flex;
    align-items: center;
    gap: 12px;
}

.dimension-badge {
    font-family: 'Courier New', monospace;
    font-size: 12px;
    font-weight: 600;
    color: #2a4a7a;
    padding: 4px 12px;
    background: rgba(42, 74, 122, 0.1);
    border: 1px solid rgba(42, 74, 122, 0.3);
    border-radius: 12px;
}

.dimension-name {
    font-family: 'Courier New', monospace;
    font-size: 16px;
    font-weight: 700;
    color: #2a2a3a;
}

.result-card-form {
    margin-top: 0;
}

.result-card-form .ant-form-item {
    margin-bottom: 20px;
}

/* 圖片上傳樣式 */
.result-card-form :deep(.ant-upload-select-picture-card) {
    width: 100%;
    height: 120px;
    border-color: rgba(42, 74, 122, 0.3);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.5);
    transition: all 0.3s ease;
}

.result-card-form :deep(.ant-upload-select-picture-card:hover) {
    border-color: rgba(42, 74, 122, 0.6);
    background: rgba(42, 74, 122, 0.05);
}

.result-card-form :deep(.ant-upload-list-picture-card) {
    margin-top: 8px;
}

.result-card-form :deep(.ant-upload-list-picture-card .ant-upload-list-item) {
    border-color: rgba(42, 74, 122, 0.3);
    border-radius: 8px;
}

.result-card-form
    :deep(.ant-upload-list-picture-card .ant-upload-list-item:hover) {
    border-color: rgba(42, 74, 122, 0.6);
}

.result-card-form :deep(.ant-upload-text) {
    font-family: 'Courier New', monospace;
    font-size: 14px;
    color: #2a4a7a;
    margin-top: 8px;
}

.result-card-form :deep(.anticon) {
    color: #2a4a7a;
    font-size: 24px;
}
</style>
